import React, { useEffect, useRef, useState, useMemo } from "react";
import { Box, Chip, Divider, Tooltip, Typography } from "@mui/material";
import { MRT_ColumnDef } from "material-react-table";
import ProductInfo from "./product-details/product-info";
import ProductFacilitiesInfo from "./product-details/product-facilities-info";
import ProductFirstProcessorInfo from "./product-details/product-first-processor-info";
import AdditionalInfo from "./product-details/additional-info";
import RawMaterialInfo from "./product-details/raw-material-info";
import { StyledButton } from "../../../../../../shared/components/buttons/custom-button";
import CustomTable from "../../../../../../shared/components/table/custom-table";
import { ActionsMenu } from "../../../../../../shared/components/table/custom-actions";
import ActionsModalPopup, {
  ActionButtonConfig,
} from "../../../../../../shared/components/modals/action-popup";
import CustomStepper from "../../../../../../shared/components/stepper/steppers";
import {
  IAdditionalInfo,
  ICertifiedProductDetails,
  ITCProduct,
  ITcProductCertifiedWeightInformation,
  ITcProductFacilitiesInformation,
  ITcProductFirstProcessorInformation,
  ITcProductRawMaterialGeographicInformation,
  ITcProductRawMaterialInformation,
  flattenCertifiedRawMaterialGeographicInformation,
  groupCertifiedRawMaterialGeographicInformation,
  mapProductFacilitiesToPayload,
  normalizeProductFacilityInformation,
  mapToPayload,
  normalizeStringArray,
  payloadToMap,
  toAdditivesString,
  toStringArray,
} from "../../../tc-models/tc-req-dto/tc-certified-product-details.req_dto";
import {
  FloatingButtonContainer,
  InnerButton,
} from "../../../../../../shared/components/buttons/floating-button-container";
import { transactionCertificateService } from "../../../tc-service/transaction-certificate.service";
import { NavLink, useNavigate, useParams } from "react-router-dom";
import useNotification from "../../../../../../shared/hooks/useNotification";
import { useFormActions } from "../../../../../../shared/hooks/form-builder.hooks";
import { useApi } from "../../../../../../shared/hooks/useApi";
import { LoaderComponent } from "../../../../../../shared/components/loaders/page-loader";
import {
  DELETECONFIG,
  TC_CREATE_ROUTE_CONFIG,
  TCAppConfig,
} from "../../../../../../shared/app-configs/tc-app-configs";
import AlertPopup from "../../../../../../shared/components/modals/alert-moadal";
import _ from "lodash";
import { useTransactionCertificateStore, useFormStore } from "../../../../../../store/transaction-certificates.store";
import { Utills } from "../../../../../../shared/app-configs/utilit";
import { ErrorHandler } from "../../../../../../shared/utils/error-handler.utils";
import { translateTcV4ValidationDetails } from "../../../../../../shared/utils/tc-v4-validation-messages.utils";
import {
  certifiedWeightClaimFromRow,
  certifiedWeightRowsFromResponseData,
  validateProductClaimsCombinations,
} from "./product-details/claims-category.utils";
import { useTcTabErrorStore } from "../../../../../../store/tcTabErrors.store";
import RenderDetailPanel from "./render-detail-panel";
import {
  getShipmentIdsByNumbers,
  getShipmentIdsByProductId,
  mapProductData,
  mapProductDataToStepData,
  ProductShipmentResponse,
} from "./certified-products.model";
import { ApiShipmentResponseItem } from "../shipments/shipments.model";
import { fetchProductaddDetails } from "./product-api";
import { dtrackItService } from "../../../tc-service/dtrackit.service";
import { fetchIncomingTcDetailsList } from "../incoming-tc-details/incoming-details-apis";
import ProductCertifiedWeight from "./product-details/product-certified-weight";
import ProductGeoInfo from "./product-details/product-geo-info";
import ComponentWorkspace from "./component-workspace";
import ProductTotalsSummary from "./product-details/product-totals-summary";
import {
  ITcProductComponentInformation,
  mapComponentToPayload,
} from "../../../tc-models/tc-req-dto/tc-certified-product-details.req_dto";
import {
  deriveProductCertifiedWeight,
  deriveProductRawMaterials,
  deriveProductTotals,
  generateProductId,
  makeIsCertifiedRM,
  mapPersistedComponentToInMemory,
} from "./product-details/component-payload";
import { standardCodeFromValue } from "../incoming-tc-details/incoming-details-apis";
import { formatTcV4Decimal, roundTcV4Decimal } from "../../../tc-v4-decimals";

const extractProductStandard = (code: string): string => {
  if (!code) return "";
  const normalized = code.trim();
  const upper = normalized.toUpperCase();
  if (upper.startsWith("MATERIALS MATTER") || upper.startsWith("MATERIAL MATTER")) return "MMS";
  if (normalized.toLowerCase() === "na") return "na";
  if (normalized.startsWith("RAS")) return "RAS";
  if (normalized.startsWith("RWS")) return "RWS";
  if (normalized.startsWith("GRS")) return "GRS";
  if (normalized.startsWith("RCS")) return "RCS";
  if (normalized.startsWith("OCS")) return "OCS";
  const parts = normalized.split(/[\s-]/);
  return parts[0]?.trim().toUpperCase() || normalized.toUpperCase();
};

// ===== INTERFACES =====
interface CertifiedProductsComponentsProps {
  onNextTab: () => Promise<void>;
}

interface ProductFormData {
  productInfo: ITCProduct;
  geographicInfo: ITcProductRawMaterialGeographicInformation[];
  additionalInfo: IAdditionalInfo;
  rawMaterialInfo: ITcProductRawMaterialInformation[];
  productCertifiedWeightInfo: ITcProductCertifiedWeightInformation[];
  productFacilitiesInfo: ITcProductFacilitiesInformation[];
  productFirstProcessorInfo: ITcProductFirstProcessorInformation[];
}

// ===== CONSTANTS =====
const MAX_PRODUCTS_ALLOWED = 100;
const PRODUCT_LIMIT_ERROR =
  "Maximum of 100 products allowed per TC. Please remove some products before adding new ones.";
const DISALLOWED_PRODUCT_PLACEHOLDERS = new Set([
  "na",
  "n/a",
  "not available",
  "null",
]);

const normalizeProductValue = (value: unknown) =>
  String(value ?? "").trim().toLowerCase();

const getProductRow = (row: any) => row?.original ?? row ?? {};

const isDisallowedProductPlaceholder = (value: unknown) => {
  const normalized = normalizeProductValue(value);
  return !normalized || DISALLOWED_PRODUCT_PLACEHOLDERS.has(normalized);
};

const normalizePercentageTotal = (value: number) =>
  roundTcV4Decimal(value);

const formatPercentageTotal = (value: number) =>
  normalizePercentageTotal(value).toString();

const parseProductListResponse = (value: any): any[] => {
  const raw = value?.response ?? value;
  if (Array.isArray(raw)) return raw;
  if (typeof raw !== "string") return [];
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const getProductClaimsCategories = (productRecord: any): string[] => {
  const product = productRecord?.tcProductInformation?.[0] ?? productRecord ?? {};
  const certifiedWeightClaims = Array.isArray(product.tcProductCertifiedWeightInformation)
    ? product.tcProductCertifiedWeightInformation.map((row: any) => row?.tcProductClaimsCategory)
    : [];
  const rawMaterialClaims = Array.isArray(product.tcProductRawMaterialInformation)
    ? product.tcProductRawMaterialInformation.map((row: any) => row?.claimsCategory)
    : [];
  return [...certifiedWeightClaims, ...rawMaterialClaims]
    .map((value) => String(value || "").trim())
    .filter(Boolean);
};

const toProcessCodeArray = (value: unknown): string[] =>
  (Array.isArray(value) ? value : [value])
    .map((code) => String(code || "").trim().toUpperCase())
    .filter(Boolean);

// ===== MAIN COMPONENT =====
const CertifiedProductsComponents: React.FC<
  CertifiedProductsComponentsProps
> = ({ onNextTab }) => {
  // ===== STATE MANAGEMENT ====
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [editingIndex, setEditingIndex] = useState<number>(-1);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [apiShipmentsData, setAPIData] = useState<any[]>([]);
  const [pendingSave, setPendingSave] = useState<any | null>(null);
  const [isEdit, setIsEdit] = useState<boolean>(false);
  const [productId, setProductId] = useState<string | null>(null);
  const [shipmentId, setShipmentId] = useState<string | null>(null);
  const [shipmentNo, setShipmentNo] = useState<string | null>(null);
  const [originalProductArticleNo, setOriginalProductArticleNo] =
    useState<string>("");
  const [originalShipmentIds, setOriginalShipmentIds] = useState<string[]>([]);

  // UI state
  const [tcProductArticleNo, setTcProductArticleNo] = useState<string>("");
  const [popUpMessage, setPopUpMessage] = useState<string>("");
  const [popUpTitle, setPopUpTitle] = useState<string>("");
  const [popUpBtnName, setPopUpBtnName] = useState<string>("");
  const [isNextEnabled, setNextEnabled] = useState<boolean>(false);
  const [disableOnEdit, setDisableOnEdit] = useState<boolean>(false);
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });
  const [isCopy, setIsCopy] = useState<boolean>(false);
  const [isRawMaterialValid, setIsRawMaterialValid] = useState<boolean>(false);

  // ===== WITH-COMPONENT FLOW STATE =====
  // isWithComponent  — the open product modal is in "with component" mode.
  // showWorkspace    — the Component Creation Workspace replaces the stepper (full-width).
  // components       — in-memory list of components for the current product (drives derivation).
  // withComponentProductId / withComponentArticleNo — the client-generated productId + the
  //                    product's article number that link posted components to the product.
  const [isWithComponent, setIsWithComponent] = useState<boolean>(false);
  const [showWorkspace, setShowWorkspace] = useState<boolean>(false);
  const [components, setComponents] = useState<ITcProductComponentInformation[]>([]);
  const [withComponentProductId, setWithComponentProductId] = useState<string>("");
  const [withComponentArticleNo, setWithComponentArticleNo] = useState<string>("");
  const [componentSaving, setComponentSaving] = useState<boolean>(false);

  // Data state
  const [shipmentData, setShipmentData] = useState<ApiShipmentResponseItem[]>(
    []
  );
  const [tableData, setTableData] = useState<ProductShipmentResponse[]>([]);
  const [rowCount, setRowCount] = useState(0);
  const selectedTcStandards = useFormStore(
    (state) => state.pages["Transaction-Details"]?.formvalues?.tcStandard
  );
  const tcStandardsCache = useMemo(() => {
    const standardValue = selectedTcStandards;
    return Array.isArray(standardValue)
      ? standardValue
      : standardValue
      ? [String(standardValue)]
      : [];
  }, [selectedTcStandards]);
  const [certifiedRawMaterialCodes, setCertifiedRawMaterialCodes] = useState<any[]>([]);
  const [isInitialLoading, setIsInitialLoading] = useState<boolean>(true);
  const [incomingTcs, setIncomingTcs] = useState<any[]>([]);
  const [detailedProducts, setDetailedProducts] = useState<any[]>([]);
  const [hasFetchedIncomingTcs, setHasFetchedIncomingTcs] = useState<boolean>(false);
  const [hasFetchedDetailedProducts, setHasFetchedDetailedProducts] = useState<boolean>(false);

  // ===== HOOKS =====
  const navigate = useNavigate();
  const { TE_ID, formtype } = useParams<string>();
  const hasCreateTc: boolean = formtype === "create-tc";
  const hasSubmitTc: boolean = formtype === "submited-tc";
  const hasRejectedTc: boolean = formtype === "rejected-tc";
  const {
    loading,
    error,
    executeGet,
    setLoadingState,
    executePost,
    executeDelete,
  } = useApi();
  const { loading: nextLoader, executeGet: executeShipment } = useApi();
  const { notify } = useNotification();

  // Surface field-level validation details from the product/component save
  // APIs (e.g. "Expected state/province code format CC-X to CC-XXX") instead
  // of a generic "Bad Request" toast. Backend JSON property paths are
  // translated to UI field names before display.
  const notifySaveValidationErrors = (source: any, fallback: string) => {
    const details = ErrorHandler.extractValidationDetails(source);
    if (details.length > 0) {
      // Show every message — collapsing the tail into "and N more validation
      // issues" hid the remaining reasons from testers (86ajjf3u1).
      translateTcV4ValidationDetails(details).forEach((message) =>
        notify(message, "error")
      );
      return;
    }
    notify(ErrorHandler.extractErrorMessage(source, fallback), "error");
  };
  const { isRestrictPopUpSave, setRestrictPopUpSave, getTCStandardGOTS, returnTcProducts, sellerScStatus = "" } =
    useTransactionCertificateStore();
  // Read RMDF violation data written by IncomingTcDetails — mirrors the SC v4
  // facility-details pattern where facilityDetailsCache is written by the prefetch
  // hook and read by facility-details to show violation banners.
  const { rmdfViolations } = useTcTabErrorStore();
  const {
    getCurrentFormValues,
    getCurrentTableData,
    updateTableData,
    replaceTableDatas,
    updateTableRowbyIndex,
    removeTableRowbyIndex,
    updateField,
    setDeleteSave,
    setEditSave,
    resetFormValueByPage,
    getDeleteSave,
  } = useFormActions("certified-products");
  const { getCurrentFormValues: getTransactionDetailsFormValues } =
    useFormActions("Transaction-Details");
  const isReturn = getTransactionDetailsFormValues()?.tcIsReturn === "Yes";

  // ===== INITIAL DATA =====
  const initTcProduct: ITCProduct = {
    tcProductOrderNo: [],
    tcProductArticleNo: "",
    tcProductProductionDate: "",
    tcProductShipmentDocNo: [],
    tcProductNumberOfUnits: "",
    tcProductUnitsMetric: "",
    tcProductUnitsMetricOther: "",
    // Weights start empty rather than 0 so the user types the real value
    // (86ajey1xz); payload mapping coerces empty back to 0.
    tcProductNetShippingWeight: "",
    tcProductSupplementaryWeight: "",
    tcProductInfoSupplementaryWeight: "",
    tcProductAdditives: "",
    tcProductAdditivesWeight: "",
    tcProductCategoryCode: "",
    tcProductCategoryUserSpecificTerm: "",
    tcProductDetailCode: "",
    tcProductDetailUserSpecificTerm: "",
    tcProductCategoryCodeDescription: "",
    tcProductDetailCodeDescription: "",
  };

  const initAdditonalInfo: IAdditionalInfo = {
    tcProductInfoAdditional: "",
    tcProductInfoFabricWeightAndConstruct: "",
    tcProductInfoFabricWeightOfFinishedProduct: "",
    tcProductInfoFiberLength: "",
    tcProductInfoYarnCount: "",
    tcProductAdditives: "",
    tcProductAdditivesWeight: "",
    tcProductFeedstock: [],
    tcProductPercentTextileWaste: "",
    tcProductPackagingFor: [],
    tcProductGtin: "",
    tcProductReclaimedMaterialSource: [],
    tcProductInfoTechnicalSpecifications: "",
    tcProductOriginOrganizationTeId: [],
    tcProductOriginFacilityTeId: [],
  };

  const [stepData, setStepData] = useState<ProductFormData>({
    productInfo: initTcProduct,
    geographicInfo: [],
    additionalInfo: initAdditonalInfo,
    rawMaterialInfo: [],
    productCertifiedWeightInfo: [],
    productFacilitiesInfo: [],
    productFirstProcessorInfo: [],
  });

  // ===== REFS =====
  const productInfoRef = useRef<any>(null);
  const additionalInfoRef = useRef<any>(null);
  const rawMaterialInfoRef = useRef<any>(null);
  const geographicInfoRef = useRef<any>(null);
  const productCertifiedWeightInfoRef = useRef<any>(null);
  const productFacilitiesInfoRef = useRef<any>(null);
  const productFirstProcessorInfoRef = useRef<any>(null);
  const productCategoryOptionsRef = useRef<any[] | null>(null);

  const formRefs = [
    productInfoRef,
    rawMaterialInfoRef,
    productCertifiedWeightInfoRef,
    geographicInfoRef,
    productFacilitiesInfoRef,
    additionalInfoRef,
  ];

  // ===== UTILITY FUNCTIONS =====
  const resetFormData = () => {
    setStepData({
      productInfo: initTcProduct,
      geographicInfo: [],
      additionalInfo: initAdditonalInfo,
      rawMaterialInfo: [],
      productCertifiedWeightInfo: [],
      productFacilitiesInfo: [],
      productFirstProcessorInfo: [],
    });
    setProductId(null);
    setShipmentId(null);
    setShipmentNo(null);
    setOriginalProductArticleNo("");
    setOriginalShipmentIds([]);
    setPendingSave(null);
    setEditingIndex(-1);
    setIsEdit(false);
    setIsCopy(false);
    // Reset the with-component flow.
    setIsWithComponent(false);
    setShowWorkspace(false);
    setComponents([]);
    setWithComponentProductId("");
    setWithComponentArticleNo("");
    setComponentSaving(false);
  };

  // ===== DATA HANDLERS =====
  useEffect(() => {
    const keyMap: Record<number, keyof ProductFormData> = {
      1: "rawMaterialInfo",
      2: "productCertifiedWeightInfo",
      3: "geographicInfo",
      4: "productFacilitiesInfo",
      5: "additionalInfo",
    };

    const key = keyMap[activeStep];
    if (key) {
      updateField(key, stepData[key]);
    }
  }, [activeStep]);

  //save api
  // options.componentMode  — WITH-component product: send tcProductComponentInformation:[] (the
  //                          backend rebuilds the component list from separately-posted components)
  //                          and always include the client productId so components can link to it.
  // options.keepOpen       — keep the modal open after a successful save (Step-1 "Save and Add
  //                          Components" opens the workspace instead of closing/refreshing).
  // options.productIdOverride — client-generated productId for a new WITH-component product.
  const handleSave = async (
    payload: any,
    options?: {
      componentMode?: boolean;
      keepOpen?: boolean;
      productIdOverride?: string;
    }
  ) => {
    const componentMode = options?.componentMode === true;
    const keepOpen = options?.keepOpen === true;
    const effectiveProductId = options?.productIdOverride || productId;
    try {
      setLoadingState(true);

      // ========================================
      // EXTRACT CURRENT VALUES FROM PAYLOAD
      // ========================================

      const productInfo = payload.tcProductInformation?.[0];
      if (!productInfo) {
        notify("Invalid product information", "error");
        setLoadingState(false);
        return false;
      }

      const combinationValidation = validateProductClaimsCombinations({
        isWithComponent: componentMode || isWithComponent,
        rawMaterials: productInfo.tcProductRawMaterialInformation || stepData.rawMaterialInfo,
        certifiedWeights: productInfo.tcProductCertifiedWeightInformation || stepData.productCertifiedWeightInfo,
        components: (componentMode || isWithComponent) ? components : undefined,
        articleNo: productInfo.tcProductArticleNo,
      });
      if (!combinationValidation.isValid && combinationValidation.errorMessage) {
        notify(combinationValidation.errorMessage, "error");
        setLoadingState(false);
        return false;
      }

      const transactionDetails = getTransactionDetailsFormValues() || {};
      const isReturn = transactionDetails.tcIsReturn === "Yes";
      if (isReturn) {
        const store = useTransactionCertificateStore.getState();
        const match = (store.returnTcProducts || []).find(
          (p: any) => String(p.tcProductArticleNo || p.tcProductNo || "").trim() === String(productInfo.tcProductArticleNo || "").trim()
        );
        if (match) {
          const originalInfo = match.tcProductInformation?.[0] || match || {};
          
          // Return TC raw materials must match the original incoming product exactly.
          productInfo.tcProductRawMaterialInformation = originalInfo.tcProductRawMaterialInformation || originalInfo.rawMaterialInfo || [];

          if (!productInfo.tcProductAdditives) {
            productInfo.tcProductAdditives = originalInfo.tcProductAdditives || "";
          }
          if (!productInfo.tcProductFeedstock) {
            productInfo.tcProductFeedstock = originalInfo.tcProductFeedstock || "";
          }
          if (!productInfo.tcProductPercentTextileWaste) {
            productInfo.tcProductPercentTextileWaste = originalInfo.tcProductPercentTextileWaste || "";
          }
          if (!productInfo.tcProductNumberOfUnits) {
            productInfo.tcProductNumberOfUnits = originalInfo.tcProductNumberOfUnits || "";
          }
          if (!productInfo.tcProductUnitsMetric) {
            productInfo.tcProductUnitsMetric = originalInfo.tcProductUnitsMetric || "";
          }
          if (!productInfo.tcProductUnitsMetricOther) {
            productInfo.tcProductUnitsMetricOther = originalInfo.tcProductUnitsMetricOther || "";
          }
          if (!productInfo.tcProductInfoFiberLength) {
            productInfo.tcProductInfoFiberLength = originalInfo.tcProductInfoFiberLength || "";
          }
          if (!productInfo.tcProductInfoYarnCount) {
            productInfo.tcProductInfoYarnCount = originalInfo.tcProductInfoYarnCount || "";
          }
          if (!productInfo.tcProductInfoFabricWeightAndConstruct) {
            productInfo.tcProductInfoFabricWeightAndConstruct = originalInfo.tcProductInfoFabricWeightAndConstruct || "";
          }
          if (!productInfo.tcProductInfoFabricWeightOfFinishedProduct) {
            productInfo.tcProductInfoFabricWeightOfFinishedProduct = originalInfo.tcProductInfoFabricWeightOfFinishedProduct || "";
          }
          if (!productInfo.tcProductInfoTechnicalSpecifications) {
            productInfo.tcProductInfoTechnicalSpecifications = originalInfo.tcProductInfoTechnicalSpecifications || "";
          }
          if (!productInfo.tcProductCategoryUserSpecificTerm) {
            productInfo.tcProductCategoryUserSpecificTerm = originalInfo.tcProductCategoryUserSpecificTerm || "";
          }
          if (!productInfo.tcProductDetailUserSpecificTerm) {
            productInfo.tcProductDetailUserSpecificTerm = originalInfo.tcProductDetailUserSpecificTerm || "";
          }
          if (!productInfo.tcProductGtin) {
            productInfo.tcProductGtin = originalInfo.tcProductGtin || "";
          }
          if (!productInfo.tcProductPackagingFor || (Array.isArray(productInfo.tcProductPackagingFor) && productInfo.tcProductPackagingFor.length === 0)) {
            productInfo.tcProductPackagingFor = originalInfo.tcProductPackagingFor || [];
          }
          if (!productInfo.tcProductReclaimedMaterialSource || (Array.isArray(productInfo.tcProductReclaimedMaterialSource) && productInfo.tcProductReclaimedMaterialSource.length === 0)) {
            productInfo.tcProductReclaimedMaterialSource = originalInfo.tcProductReclaimedMaterialSource || [];
          }

          if (!productInfo.tcProductFacilityInformation || productInfo.tcProductFacilityInformation.length === 0) {
            productInfo.tcProductFacilityInformation = originalInfo.tcProductFacilityInformation || originalInfo.tcProductFacilitiesInformation || [];
          }
          if (!productInfo.tcProductFirstProcessorInformation || productInfo.tcProductFirstProcessorInformation.length === 0) {
            productInfo.tcProductFirstProcessorInformation = originalInfo.tcProductFirstProcessorInformation || [];
          }
        }
      }

      const currentArticleNo = productInfo.tcProductArticleNo || "";
      const currentShipmentNos = Array.isArray(productInfo.tcProductShipmentDocNo)
        ? productInfo.tcProductShipmentDocNo
        : [productInfo.tcProductShipmentDocNo || ""];
      const isCopyOperation = isCopy;

      // Validate article number
      if (isDisallowedProductPlaceholder(currentArticleNo)) {
        notify("Product Article Number is required", "error");
        setLoadingState(false);
        return false;
      }

      const currentArticleKey = normalizeProductValue(currentArticleNo);
      const currentGtinKey = normalizeProductValue(productInfo.tcProductGtin);
      const hasDuplicateValue = (field: "tcProductArticleNo" | "tcProductGtin", currentKey: string) =>
        Boolean(currentKey) &&
        tableData.some((row) => {
          const item = getProductRow(row);
          if (!isCopyOperation && productId && item?.productId === productId) {
            return false;
          }
          if ((item as any)?.isReturnAutofillDraft && normalizeProductValue(item?.[field]) === currentKey) {
            return false;
          }
          return normalizeProductValue(item?.[field]) === currentKey;
        });

      if (hasDuplicateValue("tcProductArticleNo", currentArticleKey)) {
        notify("Product Article Number must be unique across all products", "error");
        setLoadingState(false);
        return false;
      }

      if (currentGtinKey && hasDuplicateValue("tcProductGtin", currentGtinKey)) {
        notify("Product GTIN must be unique across all products", "error");
        setLoadingState(false);
        return false;
      }

      const packagingTargets = new Set<string>([
        "non-certified product",
        currentArticleKey,
        ...tableData
          .map((row) => normalizeProductValue(getProductRow(row)?.tcProductArticleNo))
          .filter(Boolean),
      ]);
      const invalidPackagingFor = normalizeStringArray(productInfo.tcProductPackagingFor)
        .find((item) => isDisallowedProductPlaceholder(item) || !packagingTargets.has(normalizeProductValue(item)));

      if (invalidPackagingFor) {
        notify("Product Packaging For must be Non-Certified Product or an existing Product Article Number", "error");
        setLoadingState(false);
        return false;
      }

      // ========================================
      // MAP SHIPMENT NUMBERS TO IDs
      // ========================================

      const currentShipmentIds = currentShipmentNos
        .map((shipmentNo: string) => {
          const shipment = getShipmentIdsByNumbers(shipmentData, shipmentNo);
          return shipment?.shipmentId;
        })
        .filter((id: string | undefined): id is string => !!id);

      // Validate shipment IDs
      if (currentShipmentIds.length === 0) {
        notify(
          `No valid shipment IDs found for shipment numbers: ${currentShipmentNos.join(
            ", "
          )}`,
          "error"
        );
        setLoadingState(false);
        return false;
      }

      // ========================================
      // SCENARIO DETECTION LOGIC
      // ========================================

      const isNewProduct = !productId || productId === "";

      // Compare shipment IDs (arrays must be sorted for comparison)
      const currentShipmentIdsSorted = [...currentShipmentIds].sort();
      const originalShipmentIdsSorted = [...originalShipmentIds].sort();
      const shipmentChanged =
        originalShipmentIds.length > 0 &&
        JSON.stringify(currentShipmentIdsSorted) !==
        JSON.stringify(originalShipmentIdsSorted);

      // Check if article number changed
      const articleNoChanged =
        originalProductArticleNo &&
        originalProductArticleNo !== currentArticleNo;

      let oldShipmentIds: string[];
      let oldArticleNo: string;
      let scenario = "";

      // ========================================
      // DETERMINE SCENARIO
      // ========================================

      if (isNewProduct || isCopyOperation) {
        // SCENARIO 1: New Product + New Shipment;
        oldShipmentIds = currentShipmentIds;
        oldArticleNo = currentArticleNo;
      } else if (shipmentChanged && !articleNoChanged) {
        // SCENARIO 2: Old Product - Edit - Change Associated Shipment Docs
        oldShipmentIds = originalShipmentIds;
        oldArticleNo = originalProductArticleNo || currentArticleNo;
      } else if (!shipmentChanged && articleNoChanged) {
        // SCENARIO 3: Old Product - Edit - Change Product Article No
        oldShipmentIds =
          originalShipmentIds.length > 0
            ? originalShipmentIds
            : currentShipmentIds;
        oldArticleNo = originalProductArticleNo;
      } else if (shipmentChanged && articleNoChanged) {
        // SCENARIO 4: Old Product - Edit - Change Both Shipment and Article No
        oldShipmentIds = originalShipmentIds;
        oldArticleNo = originalProductArticleNo;
      } else {
        // FALLBACK: Edit without changes (same as Scenario 2/3 logic)
        oldShipmentIds =
          originalShipmentIds.length > 0
            ? originalShipmentIds
            : currentShipmentIds;
        oldArticleNo = originalProductArticleNo || currentArticleNo;
      }

      // ========================================
      // BUILD QUERY PARAMETERS
      // ========================================

      const queryParams = new URLSearchParams({
        shipmentId: JSON.stringify(currentShipmentIds), // Current/new shipment IDs
        oldShipmentId: JSON.stringify(oldShipmentIds), // Original shipment IDs
        oldProductArticleNo: oldArticleNo, // Original article number
      });

      const apiUrl = `${TCAppConfig.POST_PRODUCT_INFO_V4
        }?${queryParams.toString()}`;

      // ========================================
      // BUILD CLEAN PAYLOAD
      // ========================================

      const cleanPayload = {
        tcId: TE_ID,
        // WITH-component always sends the client productId (so components can link to it);
        // without-component keeps the existing edit-only behaviour.
        ...(componentMode
          ? effectiveProductId
            ? { productId: effectiveProductId }
            : {}
          : !isCopyOperation && productId
          ? { productId }
          : {}),
        tcProductInformation: [
          {
            tcProductOrderNo: Array.isArray(productInfo.tcProductOrderNo)
              ? productInfo.tcProductOrderNo
              : [productInfo.tcProductOrderNo || ""],
            tcProductArticleNo: productInfo.tcProductArticleNo || "",
            tcProductProductionDate: productInfo.tcProductProductionDate || "",
            tcProductShipmentDocNo: Array.isArray(productInfo.tcProductShipmentDocNo)
              ? productInfo.tcProductShipmentDocNo
              : [productInfo.tcProductShipmentDocNo || ""],
            tcProductNumberOfUnits:
              productInfo.tcProductNumberOfUnits?.toString() || "0",
            tcProductUnitsMetric: productInfo.tcProductUnitsMetric || "",
            tcProductUnitsMetricOther: productInfo.tcProductUnitsMetricOther || "",
            tcProductNetShippingWeight:
              parseFloat(
                productInfo.tcProductNetShippingWeight?.toString() || "0"
              ) || 0,
            tcProductSupplementaryWeight:
              parseFloat(
                productInfo.tcProductSupplementaryWeight?.toString() || "0"
              ) || 0,
            tcProductInfoSupplementaryWeight:
              productInfo.tcProductInfoSupplementaryWeight || "",
            tcProductCategoryCode: productInfo.tcProductCategoryCode || "",
            tcProductCategoryUserSpecificTerm:
              productInfo.tcProductCategoryUserSpecificTerm || "",
            tcProductDetailCode: productInfo.tcProductDetailCode || "",
            tcProductDetailUserSpecificTerm:
              productInfo.tcProductDetailUserSpecificTerm || "",
            tcProductCategoryCodeDescription:
              productInfo.tcProductCategoryCodeDescription || "",
            tcProductDetailCodeDescription:
              productInfo.tcProductDetailCodeDescription || "",
            tcProductRawMaterialInformation: (
              productInfo.tcProductRawMaterialInformation || []
            ).map((item: any) => {
              const pctRaw =
                parseFloat(
                  item.tcProductRawMaterialPercentage?.toString() || "0"
                ) || 0;
              const pctRounded = roundTcV4Decimal(pctRaw);
              return {
                tcProductRawMaterialCode: item.tcProductRawMaterialCode || "",
                tcProductRawMaterialPercentage: pctRounded,
                tcProductRawMaterialUserSpecificTerm:
                  item.tcProductRawMaterialUserSpecificTerm || "",
                tcProductRawMaterialCodeDescription:
                  item.tcProductRawMaterialCodeDescription || "",
                // Step 2 extras persisted for reload (stripped from final JSON by backend whitelist)
                claimsCategory: item.claimsCategory || "",
                certified: !!item.certified,
              };
            }),
            tcProductCertifiedRawMaterialGeographicInformation:
              groupCertifiedRawMaterialGeographicInformation(
                productInfo.tcProductCertifiedRawMaterialGeographicInformation ||
                []
              ),
            tcProductCertifiedWeightInformation: (
              productInfo.tcProductCertifiedWeightInformation || []
            ).map((item: any) => ({
              tcProductClaimsCategory: item.tcProductClaimsCategory || "",
              tcProductCertifiedWeight:
                parseFloat(
                  (
                    item.tcProductClaimsCategoryCertifiedWeight ??
                    item.tcProductCertifiedWeight ??
                    "0"
                  ).toString()
                ) || 0,
              tcProductClaimsCategoryCertifiedWeight:
                parseFloat(
                  (
                    item.tcProductClaimsCategoryCertifiedWeight ??
                    item.tcProductCertifiedWeight ??
                    "0"
                  ).toString()
                ) || 0,
              tcProductRawMaterialCodeClaimsCategory:
                item.tcProductRawMaterialCodeClaimsCategory || "",
              tcProductRawMaterialCodeClaimsCategoryDescription:
                item.tcProductRawMaterialCodeClaimsCategoryDescription || "",
            })),
            tcProductFacilityInformation: mapProductFacilitiesToPayload(
              productInfo.tcProductFacilityInformation ||
              productInfo.tcProductFacilitiesInformation ||
              []
            ),
            tcProductFirstProcessorInformation: (
              productInfo.tcProductFirstProcessorInformation || []
            ).map((item: any) => ({
              tcProductFirstProcessorTeId:
                item.tcProductFirstProcessorTeId || "",
              tcProductFirstProcessCode: toStringArray(
                item.tcProductFirstProcessCode
              ),
            })),
            tcProductAdditives: productInfo.tcProductAdditives || "",
            tcProductAdditivesWeight:
              parseFloat(
                productInfo.tcProductAdditivesWeight?.toString() || "0"
              ) || 0,
            tcProductInfoFiberLength:
              productInfo.tcProductInfoFiberLength || "",
            tcProductInfoYarnCount: productInfo.tcProductInfoYarnCount || "",
            tcProductInfoFabricWeightAndConstruct:
              productInfo.tcProductInfoFabricWeightAndConstruct || "",
            tcProductInfoFabricWeightOfFinishedProduct:
              productInfo.tcProductInfoFabricWeightOfFinishedProduct || "",
            tcProductInfoTechnicalSpecifications:
              productInfo.tcProductInfoTechnicalSpecifications || "",
            tcProductFeedstock: productInfo.tcProductFeedstock || "",
            tcProductPercentTextileWaste:
              productInfo.tcProductPercentTextileWaste || "",
            tcProductPackagingFor: normalizeStringArray(productInfo.tcProductPackagingFor),
            tcProductGtin: productInfo.tcProductGtin || "",
            tcProductReclaimedMaterialSource: Array.isArray(
              productInfo.tcProductReclaimedMaterialSource
            )
              ? productInfo.tcProductReclaimedMaterialSource.map(
                (item: string) => {
                  const raw = String(item).trim().toUpperCase();
                  const pcPattern = /^PC\d{4}$/;
                  const digitsPattern = /^\d{4}$/;
                  if (digitsPattern.test(raw)) {
                    return `PC${raw}`;
                  } else if (pcPattern.test(raw)) {
                    return raw;
                  } else {
                    return raw;
                  }
                }
              )
              : Array.isArray(productInfo.tcProductReclaimedMaterialSource)
                ? productInfo.tcProductReclaimedMaterialSource
                : [],
            tcProductInfoAdditional: productInfo.tcProductInfoAdditional || "",
            tcProductOriginOrganizationTeId: Array.isArray(
              productInfo.tcProductOriginOrganizationTeId
            )
              ? productInfo.tcProductOriginOrganizationTeId
              : productInfo.tcProductOriginOrganizationTeId
                ? [productInfo.tcProductOriginOrganizationTeId]
                : [],
            tcProductOriginFacilityTeId: Array.isArray(
              productInfo.tcProductOriginFacilityTeId
            )
              ? productInfo.tcProductOriginFacilityTeId
              : productInfo.tcProductOriginFacilityTeId
                ? [productInfo.tcProductOriginFacilityTeId]
                : [],
            // Without-component (TC v4): the backend product model always expects a component
            // array. Submit exactly one "na" component so the final-JSON validator treats this
            // product as "without component" (_product_without_components / na rule).
            // WITH-component: send [] — the final JSON is overwritten by the separately-posted
            // components (with a "na" safety net if none), so we must NOT send the "na" marker.
            tcProductComponentInformation: componentMode
              ? []
              : [{ tcProductComponentNo: "na" }],
          },
        ],
      };

      // ========================================
      // EXECUTE API CALL
      // ========================================

      const result: any = await executePost(apiUrl, cleanPayload);

      if (result.status === 200) {
        notify("Product saved successfully", "success");

        // Query the product list to find the exact productId stored in the database
        let realProductId = effectiveProductId || productId;
        try {
          const listResp: any = await executeGet(
            TCAppConfig.GET_LIST_PRODUCT_INFO_V4,
            { tcId: TE_ID, limit: 100, offset: 0 }
          );
          let listArr: any[] = [];
          const rawList = listResp?.data?.response ?? listResp?.data;
          if (typeof rawList === "string") {
            try {
              listArr = JSON.parse(rawList);
            } catch {
              listArr = [];
            }
          } else if (Array.isArray(rawList)) {
            listArr = rawList;
          }

          const dbProduct = listArr.find(
            (p: any) =>
              String(p.tcProductArticleNo || p.tcProductNo || "").trim().toLowerCase() ===
              String(productInfo.tcProductArticleNo || "").trim().toLowerCase()
          );
          if (dbProduct?.productId) {
            realProductId = dbProduct.productId;
          }
        } catch (fetchErr) {
          console.error("Failed to query final product ID from database:", fetchErr);
        }



        const transactionDetails = getTransactionDetailsFormValues() || {};
        const isReturn = transactionDetails.tcIsReturn === "Yes";
        if (isReturn) {
          const store = useTransactionCertificateStore.getState();
          const productInfo = payload.tcProductInformation?.[0] || {};
          const savedProductId = realProductId || payload.productId || effectiveProductId || productId;
          const updatedProducts = (store.returnTcProducts || []).map((p: any) => {
            if (String(p.tcProductArticleNo || p.tcProductNo || "").trim().toLowerCase() === String(productInfo.tcProductArticleNo || "").trim().toLowerCase()) {
              return {
                ...p,
                ...productInfo,
                productId: savedProductId, // Keep the new client product ID consistent!
                isReviewed: true,
                tcProductCertifiedWeightInformation: productInfo.tcProductCertifiedWeightInformation || [],
                tcProductFacilityInformation: productInfo.tcProductFacilityInformation || [],
                tcProductFirstProcessorInformation: productInfo.tcProductFirstProcessorInformation || [],
                tcProductCertifiedRawMaterialGeographicInformation: productInfo.tcProductCertifiedRawMaterialGeographicInformation || [],
                tcProductRawMaterialInformation: productInfo.tcProductRawMaterialInformation || [],
                tcProductComponentInformation: components,
              };
            }
            return p;
          });
          store.setReturnTcProducts?.(updatedProducts);
        }

        // Clear pending save if exists
        if (pendingSave) {
          setPendingSave(null);
        }

        // Step-1 "Save and Add Components": keep the modal open and let the caller open the
        // Component Creation Workspace instead of closing/refreshing the product list.
        if (keepOpen) {
          setLoadingState(false);
          return true;
        }

        // Refresh product list
        resetFormData();
        toggleDrawer();
        await fetchProductDetails();

        await onNextTab();

        // Reset form and close drawer
        setIsCopy(false);

        setLoadingState(false);
        return true;
      } else if (result.status === 409) {
        setLoadingState(false);
        notify("Product already exists with this configuration", "warning");
        return false;
      } else {
        setLoadingState(false);
        notifySaveValidationErrors(result, "Unable to save product");
        return false;
      }
    } catch (error: any) {
      console.error("Error saving product:", error);
      setLoadingState(false);
      notifySaveValidationErrors(
        error,
        "Failed to save product. Please try again."
      );
      return false;
    }
  };

  const handleDataUpdate = (
    stepKey: keyof ProductFormData,
    updatedData: any
  ) => {
    if (stepKey === "productInfo") {
      updatedData = {
        ...updatedData,
        tcProductNetShippingWeight:
          parseFloat(
            updatedData.tcProductNetShippingWeight?.toString() || "0"
          ) || 0,
        tcProductSupplementaryWeight:
          parseFloat(
            updatedData.tcProductSupplementaryWeight?.toString() || "0"
          ) || 0,
        tcProductNumberOfUnits:
          updatedData.tcProductNumberOfUnits?.toString() || "0",
        tcProductUnitsMetric: updatedData.tcProductUnitsMetric || "",
        tcProductUnitsMetricOther: updatedData.tcProductUnitsMetricOther || "",
        tcProductOrderNo: Array.isArray(updatedData.tcProductOrderNo)
          ? updatedData.tcProductOrderNo
          : [updatedData.tcProductOrderNo || ""],
        tcProductShipmentDocNo: Array.isArray(updatedData.tcProductShipmentDocNo)
          ? updatedData.tcProductShipmentDocNo
          : [updatedData.tcProductShipmentDocNo || ""],
        tcProductArticleNo: updatedData.tcProductArticleNo || "",
        tcProductProductionDate: updatedData.tcProductProductionDate || "",
        tcProductInfoSupplementaryWeight:
          updatedData.tcProductInfoSupplementaryWeight || "",
        tcProductAdditives: updatedData.tcProductAdditives || "",
        tcProductAdditivesWeight:
          parseFloat(updatedData.tcProductAdditivesWeight?.toString() || "0") ||
          0,
        tcProductCategoryCode: updatedData.tcProductCategoryCode || "",
        tcProductCategoryUserSpecificTerm:
          updatedData.tcProductCategoryUserSpecificTerm || "",
        tcProductDetailCode: updatedData.tcProductDetailCode || "",
        tcProductDetailUserSpecificTerm:
          updatedData.tcProductDetailUserSpecificTerm || "",
        tcProductCategoryCodeDescription:
          updatedData.tcProductCategoryCodeDescription || "",
        tcProductDetailCodeDescription:
          updatedData.tcProductDetailCodeDescription || "",
      };
    } else if (stepKey === "rawMaterialInfo") {
      updatedData = updatedData.map(
        (item: ITcProductRawMaterialInformation) => ({
          ...item,
          tcProductRawMaterialPercentage:
            parseFloat(
              item.tcProductRawMaterialPercentage?.toString() || "0"
            ) || 0,
          tcProductRawMaterialCode: item.tcProductRawMaterialCode || "",
          tcProductRawMaterialUserSpecificTerm:
            item.tcProductRawMaterialUserSpecificTerm || "",
          tcProductRawMaterialCodeDescription:
            item.tcProductRawMaterialCodeDescription || "",
        })
      );
    } else if (stepKey === "geographicInfo") {
      updatedData = updatedData.map(
        (item: ITcProductRawMaterialGeographicInformation) => ({
          ...item,
          tcProductCertifiedRawMaterialWeight:
            parseFloat(
              item.tcProductCertifiedRawMaterialWeight?.toString() || "0"
            ) || 0,
          tcProductCertifiedRawMaterialCode:
            item.tcProductCertifiedRawMaterialCode || "",
          tcProductCertifiedRawMaterialCodeDescription:
            item.tcProductCertifiedRawMaterialCodeDescription || "",
          tcProductCertifiedRawMaterialCountryOrArea:
            item.tcProductCertifiedRawMaterialCountryOrArea || "",
          tcProductCertifiedRawMaterialCountryOrAreaDescription:
            item.tcProductCertifiedRawMaterialCountryOrAreaDescription || "",
          tcProductCertifiedRawMaterialStateOrProvince:
            item.tcProductCertifiedRawMaterialStateOrProvince || "",
          tcProductCertifiedRawMaterialStateOrProvinceDescription:
            item.tcProductCertifiedRawMaterialStateOrProvinceDescription || "",
        })
      );
    } else if (stepKey === "productCertifiedWeightInfo") {
      updatedData = updatedData.map(
        (item: ITcProductCertifiedWeightInformation) => ({
          ...item,
          tcProductCertifiedWeight:
            parseFloat(item.tcProductCertifiedWeight?.toString() || "0") || 0,
          tcProductClaimsCategoryCertifiedWeight:
            parseFloat(
              (
                item.tcProductClaimsCategoryCertifiedWeight ??
                item.tcProductCertifiedWeight ??
                "0"
              ).toString()
            ) || 0,
          tcProductClaimsCategory: item.tcProductClaimsCategory || "",
          tcProductRawMaterialCodeClaimsCategory:
            item.tcProductRawMaterialCodeClaimsCategory || "",
          tcProductRawMaterialCodeClaimsCategoryDescription:
            item.tcProductRawMaterialCodeClaimsCategoryDescription || "",
        })
      );
    } else if (stepKey === "additionalInfo") {
      const { tcProductOriginOrganizationTeIdSearch, tcProductOriginFacilityTeIdSearch, ...additionalInfoValues } =
        updatedData;
      updatedData = {
        ...additionalInfoValues,
        tcProductAdditivesWeight:
          parseFloat(updatedData.tcProductAdditivesWeight?.toString() || "0") ||
          0,
        tcProductAdditives: updatedData.tcProductAdditives || "",
        tcProductInfoFiberLength: updatedData.tcProductInfoFiberLength || "",
        tcProductInfoYarnCount: updatedData.tcProductInfoYarnCount || "",
        tcProductInfoFabricWeightAndConstruct:
          updatedData.tcProductInfoFabricWeightAndConstruct || "",
        tcProductInfoFabricWeightOfFinishedProduct:
          updatedData.tcProductInfoFabricWeightOfFinishedProduct || "",
        tcProductInfoTechnicalSpecifications:
          updatedData.tcProductInfoTechnicalSpecifications || "",
        tcProductFeedstock: updatedData.tcProductFeedstock || "",
        tcProductPercentTextileWaste:
          updatedData.tcProductPercentTextileWaste || "",
        tcProductPackagingFor: normalizeStringArray(updatedData.tcProductPackagingFor),
        tcProductGtin: updatedData.tcProductGtin || "",
        tcProductReclaimedMaterialSource:
          updatedData.tcProductReclaimedMaterialSource || "",
        tcProductInfoAdditional: updatedData.tcProductInfoAdditional || "",
        tcProductOriginOrganizationTeId: Array.isArray(
          updatedData.tcProductOriginOrganizationTeId
        )
          ? updatedData.tcProductOriginOrganizationTeId
          : updatedData.tcProductOriginOrganizationTeId
            ? [String(updatedData.tcProductOriginOrganizationTeId)]
            : [],
        tcProductOriginFacilityTeId: Array.isArray(
          updatedData.tcProductOriginFacilityTeId
        )
          ? updatedData.tcProductOriginFacilityTeId
          : updatedData.tcProductOriginFacilityTeId
            ? [String(updatedData.tcProductOriginFacilityTeId)]
            : [],
      };
    } else if (stepKey === "productFacilitiesInfo") {
      updatedData = updatedData.map(
        (item: ITcProductFacilitiesInformation) => ({
          ...item,
          tcProductFacilitiesTeId: item.tcProductFacilitiesTeId || "",
          tcProductFacilitiesProcessCode:
            item.tcProductFacilitiesProcessCode || "",
        })
      );
    } else if (stepKey === "productFirstProcessorInfo") {
      updatedData = updatedData.map(
        (item: ITcProductFirstProcessorInformation) => ({
          ...item,
          tcProductFirstProcessorTeId: item.tcProductFirstProcessorTeId || "",
          // Multi-select array (mirrors facility process code); keep as a normalized array.
          tcProductFirstProcessCode: toStringArray(item.tcProductFirstProcessCode),
        })
      );
    }
    setStepData((prev) => ({
      ...prev,
      [stepKey]: updatedData,
    }));
    updateField(stepKey, updatedData);
  };

  const fetchShipmentData = async () => {
    try {
      // Assuming TCAppConfig has been updated with a new key for the v4 endpoint, e.g., TCAppConfig.GET_SHIPMENT_CONSIGNEE_DETAILS_V4
      // Replace with the actual config key/path as per your app config
      const response = await executeShipment(
        TCAppConfig.GET_SHIPMENTS_ENDPOINT_V4,
        {
          tcId: TE_ID,
        }
      );
      const shipments = response?.data.response || [];
      // Fetched shipment data
      setShipmentData(shipments);
    } catch (err) {
      console.error("Failed to fetch shipment data:", err);
      notify("Failed to fetch shipment data", "error");
    }
  };



  const fetchProductDetails = async (isFirstLoad = false) => {
    try {
      if (isFirstLoad) {
        setIsInitialLoading(true);
      }
      const offset = pagination.pageIndex * pagination.pageSize;
      const response: any = await executeGet(
        TCAppConfig.GET_LIST_PRODUCT_INFO_V4,
        { tcId: TE_ID, limit: pagination.pageSize, offset }
      );

      let result = response.data;
      let productCategories = await getProductCategory();

      // Handle response - it might be a string, array, or undefined
      let responseArray: any[] = [];

      if (result?.response) {
        // If response is a string, parse it
        if (typeof result.response === "string") {
          try {
            responseArray = JSON.parse(result.response);
          } catch (parseError) {
            console.error("Failed to parse response string:", parseError);
            responseArray = [];
          }
        }
        // If response is already an array, use it
        else if (Array.isArray(result.response)) {
          responseArray = result.response;
        }
        // Otherwise, default to empty array
        else {
          responseArray = [];
        }
      }

      const transactionDetails = getTransactionDetailsFormValues() || {};
      const isReturn = transactionDetails.tcIsReturn === "Yes";
      const storeStateReturnProducts = useTransactionCertificateStore.getState().returnTcProducts;
      const returnProducts = storeStateReturnProducts !== undefined && storeStateReturnProducts !== null ? storeStateReturnProducts : (returnTcProducts || []);
      let isLocalAutofill = false;

      if (isReturn && returnProducts.length > 0) {
        const tcKey = String(TE_ID || (returnProducts[0] as any)?.tcId || "").trim();
        const storeMap = useTransactionCertificateStore.getState().deletedReturnTcArticlesMap || {};
        const storeDeleted = storeMap[tcKey] || [];
        let localDeleted: string[] = [];
        if (tcKey) {
          try {
            localDeleted = JSON.parse(localStorage.getItem(`return_tc_deleted_${tcKey}`) || "[]");
          } catch (e) {
            localDeleted = [];
          }
        }
        const allDeletedArticleNos = new Set<string>([
          ...storeDeleted.map((a: string) => String(a).trim().toLowerCase()),
          ...localDeleted.map((a: string) => String(a).trim().toLowerCase()),
        ]);

        const savedArticleNos = new Set(
          responseArray.map((p: any) => String(p.tcProductArticleNo || p.tcProductNo || "").trim().toLowerCase())
        );

        const unsavedReturnProducts = returnProducts.filter((p: any) => {
          const articleKey = String(p.tcProductArticleNo || p.tcProductNo || "").trim().toLowerCase();
          if (savedArticleNos.has(articleKey)) return false;
          if (allDeletedArticleNos.has(articleKey)) return false;
          return true;
        });

        if (unsavedReturnProducts.length > 0) {
          isLocalAutofill = unsavedReturnProducts.some((p: any) => !p.isReviewed);
          const draftRows = unsavedReturnProducts.map((p: any) => ({
            productId: p.productId || "",
            shipmentId: p.shipmentId || "",
            tcProductArticleNo: p.tcProductArticleNo || p.tcProductNo || "",
            tcProductCategoryCode: p.tcProductCategoryCode || "",
            tcProductDetailCode: p.tcProductDetailCode || "",
            tcProductRawMaterialInformation: p.tcProductRawMaterialInformation || [],
            tcProductComponentInformation: p.tcProductComponentInformation || p.components || [],
            isReturnAutofillDraft: !p.isReviewed,
            tcProductNetShippingWeight: p.tcProductNetShippingWeight || p.netShippingWeight || 0,
            tcProductSupplementaryWeight: p.tcProductSupplementaryWeight || 0,
            tcProductAdditivesWeight: p.tcProductAdditivesWeight || 0,
            tcProductShipmentDocNo: p.tcProductShipmentDocNo || [],
            tcProductFacilityInformation: p.tcProductFacilityInformation || p.tcProductFacilitiesInformation || [],
            tcProductFirstProcessorInformation: p.tcProductFirstProcessorInformation || [],
            tcProductCertifiedWeightInformation: p.tcProductCertifiedWeightInformation || [],
            tcProductCertifiedRawMaterialGeographicInformation: p.tcProductCertifiedRawMaterialGeographicInformation || [],
          }));
          responseArray = [...responseArray, ...draftRows];
        }
      }


      // Only map if there are items in the array
      const mappedResponse =
        responseArray.length > 0
          ? responseArray.map((res: any) => {
            return {
              ...res,
              tcNetShippingWeight: res.tcProductNetShippingWeight,
              tcProductCategoryCodeDescription:
                productCategories.find(
                  (option: any) => option.value === res.tcProductCategoryCode
                )?.label || "",
              tcProductShipmentDocNo: Array.isArray(res.tcProductShipmentDocNo)
                ? res.tcProductShipmentDocNo
                : res.tcProductShipmentDocNo ? [res.tcProductShipmentDocNo] : [],
            };
          })
          : [];

      setTableData(mappedResponse);
      setRowCount(isLocalAutofill ? mappedResponse.length : (result?.no_of_records || 0));
      setAPIData(mappedResponse);
      setDeleteSave(false);
      setNextEnabled(!isLocalAutofill);
    } catch (err) {
      console.error("Failed to fetch product details:", err);
      notify("Failed to fetch product details", "error");
    } finally {
      setIsInitialLoading(false);
    }
  };

  useEffect(() => {
    if (TE_ID) {
      fetchProductDetails(true);
    }
  }, [TE_ID]);

  useEffect(() => {
    if (TE_ID && !isInitialLoading) {
      fetchProductDetails(false);
    }
  }, [pagination?.pageIndex, returnTcProducts]);

  const getProductCategory = async () => {
    if (productCategoryOptionsRef.current) {
      return productCategoryOptionsRef.current;
    }

    try {
      const result: any = await dtrackItService.getProductCategory();
      const productDetails = result.data.map((item: any) => ({
        value: item.code_1,
        label: `${item.description_1} (${item.code_1})`,
      }));
      productCategoryOptionsRef.current = productDetails;
      return productDetails;
    } catch (err) {
      console.error("Error fetching product categories:", err);
      return [];
    }
  };

  const tableSteps = [1, 2, 3, 4];

  // ===== STEP NAVIGATION =====
  const getStepData = (): ProductFormData =>
    getCurrentFormValues() as ProductFormData;

  // DAT-501 rows 80-83: both first-processor and facility tables are mandatory,
  // every row needs a TE-ID plus at least one process code, and TE-IDs must be unique.
  const validateProcessorAndFacilityRequirements = (
    productData: ProductFormData
  ): boolean => {
    const validateRows = (
      rows: any[],
      label: string,
      teIdField: string,
      processCodeField: string
    ): boolean => {
      if (!Array.isArray(rows) || rows.length === 0) {
        notify(`At least one ${label} entry is required`, "error");
        return false;
      }

      const seenTeIds = new Set<string>();
      for (const row of rows) {
        const teId = String(row?.[teIdField] || "").trim().toUpperCase();
        const processCodes = toProcessCodeArray(row?.[processCodeField]);
        if (!teId) {
          notify(`${label} TE-ID is required`, "error");
          return false;
        }
        if (!processCodes.length) {
          notify(`${label} Process Code is required`, "error");
          return false;
        }
        if (seenTeIds.has(teId)) {
          notify(`${label} TE-ID must be unique`, "error");
          return false;
        }
        seenTeIds.add(teId);
        if (
          teId === "TE-99999999" &&
          (processCodes.length !== 1 || processCodes[0] !== "PR9999")
        ) {
          notify(`TE-99999999 must use ${label} Process Code PR9999`, "error");
          return false;
        }
      }
      return true;
    };

    return (
      validateRows(
        productData.productFirstProcessorInfo,
        "Product First Processor",
        "tcProductFirstProcessorTeId",
        "tcProductFirstProcessCode"
      ) &&
      validateRows(
        productData.productFacilitiesInfo,
        "Product Facilities",
        "tcProductFacilitiesTeId",
        "tcProductFacilitiesProcessCode"
      )
    );
  };
  const handleNext = async () => {
    try {
      const currentFormRef = formRefs[activeStep]?.current;
      const currentStepData = getStepData();

      // Table steps (1, 2, 3, 4)
      if (tableSteps.includes(activeStep)) {
        const refsWithUnsaved = [
          {
            ref: rawMaterialInfoRef,
            data: stepData.rawMaterialInfo,
            step: 1,
            required: true,
            entryName: "Raw Material",
          },
          {
            ref: productCertifiedWeightInfoRef,
            data: stepData.productCertifiedWeightInfo,
            step: 2,
            required: true,
            entryName: "Certified Weight",
          },
          {
            ref: geographicInfoRef,
            data: stepData.geographicInfo,
            step: 3,
            required: true,
            entryName: "Geographic Information",
          },
          {
            ref: productFacilitiesInfoRef,
            data: stepData.productFacilitiesInfo,
            step: 4,
            required: true,
            entryName: "Product Facilities",
          },
          {
            ref: productFirstProcessorInfoRef,
            data: stepData.productFirstProcessorInfo,
            step: 4,
            required: true,
            entryName: "Product First Processor",
          },
        ];
        const currentRefsData = refsWithUnsaved.filter(
          (r) => r.step === activeStep
        );

        if (currentRefsData.length > 0) {
          for (const currentRefData of currentRefsData) {
            const formRef = currentRefData.ref.current;
            const isEditing =
              formRef?.editingRowIndex !== undefined &&
              formRef.editingRowIndex !== -1;
            const hasUnsaved = formRef?.hasUnsavedRow || false;

            if (isEditing) {
              notify(
                "Please save or cancel the current row before proceeding",
                "error"
              );
              return;
            }

            if (hasUnsaved) {
              notify(
                "Please complete all entries and ensure totals match before proceeding",
                "error"
              );
              return;
            }

            if (
              currentRefData.required &&
              (!currentRefData.data || currentRefData.data.length === 0)
            ) {
              notify(
                `At least one ${currentRefData.entryName || steps[activeStep].label} entry is required`,
                "error"
              );
              return;
            }
          }

          if (activeStep === 1) {
            // Validate raw material percentages
            const rawMaterialData = currentStepData.rawMaterialInfo;
            const total = rawMaterialData.reduce((sum, item) => {
              const pct = Number(item.tcProductRawMaterialPercentage) || 0;
              if (item.tcProductRawMaterialCode || pct > 0) {
                return sum + pct;
              }
              return sum;
            }, 0 as number);
            const normalizedTotal = normalizePercentageTotal(total);

            // Check if total exceeds 100%
            if (normalizedTotal > 100) {
              notify(
                "Raw material percentages cannot exceed 100%. Current total: " +
                formatPercentageTotal(normalizedTotal) +
                "%",
                "error"
              );
              return;
            }

            // Check if there are any raw materials entered but total is less than 100%
            const hasRawMaterialsEntered = rawMaterialData.some(
              (item) =>
                item.tcProductRawMaterialCode ||
                Number(item.tcProductRawMaterialPercentage ?? 0) > 0
            );

            if (hasRawMaterialsEntered && normalizedTotal < 100) {
              notify(
                "Raw material percentages must total 100%. Current total: " +
                formatPercentageTotal(normalizedTotal) +
                "%",
                "error"
              );
              return;
            }

            // Enforce the wireframe rule: certified raw materials need a claims category and
            // only one claims category is allowed per standard.
            const rmRef = rawMaterialInfoRef.current;
            if (rmRef && typeof rmRef.validateClaimsCategories === "function") {
              if (!rmRef.validateClaimsCategories()) {
                return;
              }
            }
          }

          // Geographic Info - Validate sum of certified weights matches total
          if (activeStep === 3) {
            const geoData = currentStepData.geographicInfo;
            const rawData = currentStepData.rawMaterialInfo;

            // Group geographic weights by raw material code
            const geosByCode = new Map<string, number>();
            geoData.forEach((geo: any) => {
              const code = geo.tcProductCertifiedRawMaterialCode;
              const weight =
                Number(geo.tcProductCertifiedRawMaterialWeight) || 0;
              geosByCode.set(code, (geosByCode.get(code) || 0) + weight);
            });

            // Helper function to check if raw material is certified
            const isCertifiedRM = makeIsCertifiedRM(
              certifiedRawMaterialCodes,
              tcStandardsCache
            );

            // Calculate expected weights and validate - ONLY for certified raw materials
            // (weight fields may hold "" until typed — coerce for the arithmetic)
            const netWeight = Number(currentStepData.productInfo.tcProductNetShippingWeight) || 0;
            const suppWeight = Number(currentStepData.productInfo.tcProductSupplementaryWeight) || 0;
            const additivesWeight = Number(currentStepData.productInfo.tcProductAdditivesWeight) || 0;

            for (const raw of rawData) {
              const code = raw.tcProductRawMaterialCode;
              
              // Skip validation for non-certified raw materials
              if (!isCertifiedRM(code)) {
                console.debug("[CertifiedProducts] Skipping validation for non-certified RM:", code);
                continue;
              }
              
              const percentage = Number(raw.tcProductRawMaterialPercentage) || 0;
              const expectedWeight = (netWeight - suppWeight - additivesWeight) * (percentage / 100);
              const actualWeight = geosByCode.get(code) || 0;

              // 0.5 kg tolerance, matching the backend submit validator
              // (DAT-501 geo split vs RM% of net - supplementary - additives).
              if (Math.abs(actualWeight - expectedWeight) > 0.5) {
                notify(
                  `Geographic weight mismatch for ${raw.tcProductRawMaterialCodeDescription || code}. ` +
                  `Expected: ${formatTcV4Decimal(expectedWeight)} kg, Actual: ${formatTcV4Decimal(actualWeight)} kg`,
                  "error"
                );
                return;
              }
            }
          }

          if (activeStep === 2) {
            const certRef = productCertifiedWeightInfoRef.current;
            if (certRef) {
              const tableSum = certRef.tableSum || 0;
              const expectedTotal = certRef.expectedTotal || 0;
              if (Math.abs(tableSum - expectedTotal) > 0.01) {
                notify(
                  `Sum of Certified Weights (${formatTcV4Decimal(tableSum)} kg) must match Total Certified Product Weight (${formatTcV4Decimal(expectedTotal)} kg)`,
                  "error"
                );
                return;
              }
            }
          }

          // Validate Product First Processor Info requirements when leaving the combined facility/processor step
          if (activeStep === 4) {
            if (!validateProcessorAndFacilityRequirements(stepData)) {
              return; // Validation failed, stay on step 5
            }
          }
        }

        // All validations for table steps passed, move to next step
        setActiveStep((prev) => Math.min(prev + 1, steps.length - 1));
        setRestrictPopUpSave(false);
        return;
      }

      // Form-based steps (0 and 5)
      if (
        (activeStep === 0 || activeStep === 5) &&
        currentFormRef &&
        typeof currentFormRef.validateForm === "function"
      ) {
        const errors = await currentFormRef.validateForm();
        Object.keys(errors).forEach((fieldName) => {
          currentFormRef.setFieldTouched(fieldName, true, false);
        });
        if (Object.keys(errors).length === 0) {
          if (typeof currentFormRef.submitForm === "function") {
            await currentFormRef.submitForm();
            const currentValues = currentFormRef.values;
            const stepKeyMap: Record<number, keyof ProductFormData> = {
              0: "productInfo",
              5: "additionalInfo",
            };
            const key = stepKeyMap[activeStep];
            if (key) {
              updateField(key, currentValues);
              setStepData((prev) => ({ ...prev, [key]: currentValues }));
            }
          }
          setActiveStep((prev) => Math.min(prev + 1, steps.length - 1));
        } else {
          notify("Please fill in the required values", "error");
        }
        setRestrictPopUpSave(false);
      }
    } catch (err) {
      console.error("Error in handleNext:", err);
      notify("An unexpected error occurred. Please try again.", "error");
    }
  };

  const handleBack = () => {
    setActiveStep((prev) => Math.max(prev - 1, 0));
    setRestrictPopUpSave(false);
  };

  const handleFinish = async () => {
    try {
      // Validate TE_ID first
      if (!TE_ID) {
        notify("TC ID is missing", "error");
        return;
      }

      // Validate mandatory first-processor and facility rows before final save.
      const fpOk = validateProcessorAndFacilityRequirements(stepData);
      if (!fpOk) return;

      let updatedStepData = { ...stepData };

      // Validate the last step
      if (additionalInfoRef.current) {
        const errors = await additionalInfoRef.current.validateForm();
        Object.keys(errors).forEach((fieldName) => {
          additionalInfoRef.current.setFieldTouched(fieldName, true, false);
        });
        if (Object.keys(errors).length > 0) {
          // Formik errors for array fields (chips) are arrays/objects; flatten
          // so every failure surfaces as a toast instead of a silent save no-op.
          const flattenErrors = (error: any): string[] =>
            typeof error === "string"
              ? (error.trim() ? [error] : [])
              : Array.isArray(error)
                ? error.flatMap(flattenErrors)
                : error && typeof error === "object"
                  ? Object.values(error).flatMap(flattenErrors)
                  : [];
          const messages = Object.values(errors).flatMap(flattenErrors);
          (messages.length
            ? messages
            : ["Please fix the Additional Info fields before saving"]
          ).forEach((message) => notify(message, "error"));
          return;
        }

        await additionalInfoRef.current.submitForm();
        const finalValues = additionalInfoRef.current.values;

        const { tcProductOriginOrganizationTeIdSearch, tcProductOriginFacilityTeIdSearch, ...additionalValues } =
          finalValues;

        const parsedAdditional = {
          ...additionalValues,
          tcProductAdditivesWeight:
            parseFloat(
              additionalValues.tcProductAdditivesWeight?.toString() || "0"
            ) || 0,
          tcProductAdditives: additionalValues.tcProductAdditives || "",
          tcProductInfoFiberLength:
            additionalValues.tcProductInfoFiberLength || "",
          tcProductInfoYarnCount: additionalValues.tcProductInfoYarnCount || "",
          tcProductInfoFabricWeightAndConstruct:
            additionalValues.tcProductInfoFabricWeightAndConstruct || "",
          tcProductInfoFabricWeightOfFinishedProduct:
            additionalValues.tcProductInfoFabricWeightOfFinishedProduct || "",
          tcProductInfoTechnicalSpecifications:
            additionalValues.tcProductInfoTechnicalSpecifications || "",
          tcProductFeedstock: additionalValues.tcProductFeedstock || "",
          tcProductPercentTextileWaste:
            additionalValues.tcProductPercentTextileWaste || "",
          tcProductPackagingFor: normalizeStringArray(additionalValues.tcProductPackagingFor),
          tcProductGtin: additionalValues.tcProductGtin || "",
          tcProductReclaimedMaterialSource:
            additionalValues.tcProductReclaimedMaterialSource || "",
          tcProductInfoAdditional:
            additionalValues.tcProductInfoAdditional || "",
          tcProductOriginOrganizationTeId: Array.isArray(
            additionalValues.tcProductOriginOrganizationTeId
          )
            ? additionalValues.tcProductOriginOrganizationTeId
            : additionalValues.tcProductOriginOrganizationTeId
              ? [String(additionalValues.tcProductOriginOrganizationTeId)]
              : [],
          tcProductOriginFacilityTeId: Array.isArray(
            additionalValues.tcProductOriginFacilityTeId
          )
            ? additionalValues.tcProductOriginFacilityTeId
            : additionalValues.tcProductOriginFacilityTeId
              ? [String(additionalValues.tcProductOriginFacilityTeId)]
              : [],
        };

        updatedStepData.additionalInfo = parsedAdditional;
        updateField("additionalInfo", parsedAdditional);
      }

      // Validate all required steps (step 5 is optional and handled by conditional validation)
      const requiredSteps = [
        {
          data: updatedStepData.rawMaterialInfo,
          name: "Raw Material Info",
          stepIndex: 1,
        },
        {
          data: updatedStepData.productCertifiedWeightInfo,
          name: "Certified Weight Info",
          stepIndex: 2,
        },
        {
          data: updatedStepData.geographicInfo,
          name: "Geographic Info",
          stepIndex: 3,
        },
        {
          data: updatedStepData.productFacilitiesInfo,
          name: "Facilities Info",
          stepIndex: 4,
        },
        // Step 5 (First Processor Info) is optional - conditional validation handles requirements
      ];

      for (const step of requiredSteps) {
        if (!step.data || step.data.length === 0) {
          setActiveStep(step.stepIndex);
          notify(`At least one entry is required for ${step.name}`, "error");
          return;
        }
      }

      // Validate Raw Material Info percentage for GOTS
      if (getTCStandardGOTS && updatedStepData.rawMaterialInfo) {
        const totalPercentage = updatedStepData.rawMaterialInfo.reduce(
          (sum, item) => {
            const percent = Number(item.tcProductRawMaterialPercentage) || 0;
            return sum + percent;
          },
          0
        );
        if (Math.abs(totalPercentage - 100) > 0.01) {
          setActiveStep(1);
          notify(
            "Total Product Raw Material Percentage should be 100",
            "error"
          );
          return;
        }
      }

      // Use mapToPayload to create the payload. For WITH-component the product re-save reuses the
      // client productId and the derived RM/certified/geo computed in handleContinueToProduct.
      const payload = mapToPayload(
        updatedStepData,
        TE_ID!,
        isWithComponent
          ? withComponentProductId
          : productId || undefined
      );

      // Call handleSave (with-component skips the "na" marker and includes the client productId).
      await handleSave(
        payload,
        isWithComponent
          ? { componentMode: true, productIdOverride: withComponentProductId }
          : undefined
      );
    } catch (err) {
      console.error("Error in handleFinish:", err);
      notify("An unexpected error occurred. Please try again.", "error");
    }
  };

  // ===== API HANDLERS =====
  // Complete handleSave for Product - Handles all 4 scenarios

  // ===== UI HANDLERS =====
  const toggleDrawer = () => {
    setActiveStep(0);
    if (drawerOpen) {
      resetFormData();
    } else {
      // Check product limit before opening drawer for new products
      if (!isEdit && rowCount >= MAX_PRODUCTS_ALLOWED) {
        notify(PRODUCT_LIMIT_ERROR, "error");
        return;
      }
    }
    setRestrictPopUpSave(false);
    setDrawerOpen((prev) => !prev);
  };

  const onAdd = () => {
    setIsEdit(false);
    resetFormData();
    toggleDrawer();
  };

  // ===== WITH-COMPONENT HANDLERS =====

  // Landing "Add Product With Component": open the product modal in with-component mode.
  const onAddWithComponent = () => {
    if (rowCount >= MAX_PRODUCTS_ALLOWED) {
      notify(PRODUCT_LIMIT_ERROR, "error");
      return;
    }
    setIsEdit(false);
    resetFormData();
    setActiveStep(0);
    setIsWithComponent(true);
    setShowWorkspace(false);
    setRestrictPopUpSave(false);
    setDrawerOpen(true);
  };

  // Step-1 "Save and Add Components": validate the product info form, generate a client productId,
  // POST the product (info only + empty RM/certified/geo + tcProductComponentInformation:[]) and
  // then open the Component Creation Workspace.
  const handleSaveProductInfoOnly = async () => {
    try {
      const currentFormRef = productInfoRef.current;
      if (!currentFormRef || typeof currentFormRef.validateForm !== "function") {
        notify("Product information form is not ready", "error");
        return;
      }
      const errors = await currentFormRef.validateForm();
      Object.keys(errors).forEach((fieldName) => {
        currentFormRef.setFieldTouched(fieldName, true, false);
      });
      if (Object.keys(errors).length > 0) {
        notify("Please fill in the required values", "error");
        return;
      }
      await currentFormRef.submitForm();

      // Net Shipping Weight + Additives are hidden/derived in with-component mode → default to 0.
      const rawValues = currentFormRef.values || {};
      const productValues = {
        ...rawValues,
        tcProductAdditivesWeight: 0,
        tcProductNetShippingWeight:
          parseFloat(rawValues.tcProductNetShippingWeight?.toString() || "0") || 0,
      };
      updateField("productInfo", productValues);
      setStepData((prev) => ({ ...prev, productInfo: productValues }));

      const articleNo = String(productValues.tcProductArticleNo || "").trim();
      const articleKey = normalizeProductValue(articleNo);
      const currentProductId = withComponentProductId || productId || undefined;
      const duplicateArticle = tableData.some((row) => {
        const product = getProductRow(row);
        if (isEdit && currentProductId && product?.productId === currentProductId) {
          return false;
        }
        if ((product as any)?.isReturnAutofillDraft && normalizeProductValue(product?.tcProductArticleNo) === articleKey) {
          return false;
        }
        return normalizeProductValue(product?.tcProductArticleNo) === articleKey;
      });
      if (duplicateArticle) {
        notify("Product Article Number must be unique across all products", "error");
        return;
      }

      // Reuse (or create) the client productId so a re-save from the workspace keeps the same id.
      const clientProductId = withComponentProductId || generateProductId();

      // Build an info-only payload (empty RM/certified/geo/facility/first-processor) for handleSave.
      const infoOnlyStepData: ProductFormData = {
        productInfo: productValues as ITCProduct,
        rawMaterialInfo: [],
        productCertifiedWeightInfo: [],
        geographicInfo: [],
        productFacilitiesInfo: [],
        productFirstProcessorInfo: [],
        additionalInfo: stepData.additionalInfo,
      };
      // Do NOT persist the product here. Its raw-material / certified-weight / geographic info are
      // DERIVED from components that don't exist yet, and post-product-info-v4 runs
      // validate_product_payload_v4 which requires at least one certified-raw-material geographic row.
      // So we only capture the client productId + article no and open the workspace: components POST
      // against this productId (the product doc is not required to exist first), and the product is
      // persisted once at the final Save with the derived geo (handleFinish) — the same validated path
      // as the without-component flow. (infoOnlyStepData is referenced for shape parity only.)
      void mapToPayload(infoOnlyStepData, TE_ID!, clientProductId);

      setWithComponentProductId(clientProductId);
      setWithComponentArticleNo(articleNo);
      setShowWorkspace(true);
    } catch (err) {
      console.error("Error in handleSaveProductInfoOnly:", err);
      notify("An unexpected error occurred. Please try again.", "error");
    }
  };

  // Workspace "Add/Edit Component" save (C-Step 3): POST post-component-detail-v4 with the client
  // productId + tcAssociatedProductArticleNo=articleNo. On 200 add/update the in-memory row.
  const handleSaveComponent = async (
    component: ITcProductComponentInformation
  ): Promise<boolean> => {
    try {
      setComponentSaving(true);
      const componentWithArticle: ITcProductComponentInformation = {
        ...component,
        tcAssociatedProductArticleNo: withComponentArticleNo,
      };

      // Duplicate component-no guard (mirrors the backend 409 on component-no + product-no).
      const dupIndex = components.findIndex(
        (c) =>
          c.tcProductComponentNo === component.tcProductComponentNo &&
          c.componentId !== component.componentId
      );
      if (dupIndex !== -1 && !component.componentId) {
        notify(
          `A component with number ${component.tcProductComponentNo} already exists for this product`,
          "warning"
        );
        setComponentSaving(false);
        return false;
      }

      const isEditComponent = Boolean(component.componentId);
      const queryParams = new URLSearchParams({
        oldProductId: withComponentProductId,
        oldProductArticleNo: withComponentArticleNo,
        sameComponentNewProductCreate: "No",
      });
      const apiUrl = `${TCAppConfig.COMPONENT_DETAILS_ENDPOINT_V4}?${queryParams.toString()}`;

      const payload = mapComponentToPayload(
        TE_ID!,
        withComponentProductId,
        componentWithArticle
      );

      const result: any = await executePost(apiUrl, payload);

      if (result?.status === 200) {
        notify("Component saved successfully", "success");
        // Persist the backend componentId when returned so subsequent edits target the same row.
        const returnedComponentId =
          result?.data?.componentId ||
          result?.data?.response?.componentId ||
          component.componentId ||
          generateProductId();
        const savedComponent: ITcProductComponentInformation = {
          ...componentWithArticle,
          componentId: returnedComponentId,
        };
        setComponents((prev) => {
          const compNo = String(savedComponent.tcProductComponentNo || "").trim().toLowerCase();
          const compId = String(savedComponent.componentId || "").trim().toLowerCase();

          const existingIndex = prev.findIndex(
            (c) =>
              (compId && String(c.componentId || "").trim().toLowerCase() === compId) ||
              (compNo && String(c.tcProductComponentNo || "").trim().toLowerCase() === compNo)
          );

          if (existingIndex !== -1) {
            const updated = [...prev];
            updated[existingIndex] = savedComponent;
            return updated;
          }
          return [...prev, savedComponent];
        });
        setComponentSaving(false);
        return true;
      } else if (result?.status === 409) {
        notify(
          "A component with this number already exists for this product",
          "warning"
        );
        setComponentSaving(false);
        return false;
      } else {
        notifySaveValidationErrors(result, "Unable to save component");
        setComponentSaving(false);
        return false;
      }
    } catch (err: any) {
      console.error("Error saving component:", err);
      notifySaveValidationErrors(err, "Failed to save component");
      setComponentSaving(false);
      return false;
    }
  };

  // Workspace component delete: Calls delete-api-v4 with exact parameters matching delete_data_v4.py logic.
  const handleDeleteComponent = async (
    component: ITcProductComponentInformation,
    index: number
  ) => {
    try {
      if (component.componentId) {
        await executeDelete(`${TCAppConfig.TC_DELETE_API_V4}`, {
          tcId: TE_ID!,
          productId: withComponentProductId || "",
          productArticleNo: withComponentArticleNo || "",
          componentId: component.componentId || "",
          componentNo: component.tcProductComponentNo || "",
        });
      }
      setComponents((prev) => prev.filter((_, i) => i !== index));
      notify("Component removed successfully", "success");
    } catch (err: any) {
      console.error("Error deleting component:", err);
      // Fallback: Remove from local state if backend call fails or component is unpersisted draft
      setComponents((prev) => prev.filter((_, i) => i !== index));
      notify("Component removed", "success");
    }
  };

  // Workspace "Continue to Product Information": derive the product-level RM / certified-weight /
  // geo from the in-memory components and populate the (read-only) product steppers, then return
  // to the product modal at Step 2.
  const handleContinueToProduct = () => {
    if (!components.length) {
      notify("Add at least one component before continuing", "error");
      return;
    }
    const supplementary =
      Number(stepData.productInfo.tcProductSupplementaryWeight) || 0;
    const totals = deriveProductTotals(components, supplementary);
    const derivedRawMaterials = deriveProductRawMaterials(components);
    const derivedCertifiedWeight = deriveProductCertifiedWeight(components);

    const combinationValidation = validateProductClaimsCombinations({
      isWithComponent: true,
      components,
      articleNo: withComponentArticleNo,
      rawMaterials: derivedRawMaterials,
      certifiedWeights: derivedCertifiedWeight,
    });
    if (!combinationValidation.isValid && combinationValidation.errorMessage) {
      notify(combinationValidation.errorMessage, "error");
      return;
    }

    setStepData((prev) => {
      const nextProductInfo: ITCProduct = {
        ...prev.productInfo,
        // Product Net Shipping (derived) = Σ component net + product supplementary.
        tcProductNetShippingWeight: totals.productNetShippingWeight,
        tcProductAdditivesWeight: 0,
      };
      const next: ProductFormData = {
        ...prev,
        productInfo: nextProductInfo,
        rawMaterialInfo: derivedRawMaterials,
        productCertifiedWeightInfo: derivedCertifiedWeight,
        // Geographic info is rebuilt by ProductGeoInfo from the derived (certified) RM rows.
        geographicInfo: prev.geographicInfo,
      };
      updateField("productInfo", nextProductInfo);
      updateField("rawMaterialInfo", derivedRawMaterials);
      updateField("productCertifiedWeightInfo", derivedCertifiedWeight);
      return next;
    });

    setShowWorkspace(false);
    setActiveStep(1); // Step 2 (Product Raw Material Information, derived/read-only)
    setRestrictPopUpSave(false);
  };

  // Exit the workspace (X / Exit) back to the product list.
  const handleExitWorkspace = () => {
    setShowWorkspace(false);
    resetFormData();
    setDrawerOpen(false);
  };

  const onCopy = (row: any) => {
    if (rowCount >= MAX_PRODUCTS_ALLOWED) {
      notify(PRODUCT_LIMIT_ERROR, "error");
      return;
    }

    setIsCopy(true);
    setIsEdit(false);

    const value = row?.original as ProductShipmentResponse;

    setProductId(null);
    setShipmentId(null);

    setOriginalProductArticleNo("");
    setOriginalShipmentIds([]);

    setShipmentNo(
      value?.tcProductShipmentDocNo && value?.tcProductShipmentDocNo?.length > 0
        ? value?.tcProductShipmentDocNo[0]
        : null
    );

    if (TE_ID && value.productId) {
      fetchProductaddDetails(
        executeGet,
        TE_ID,
        value.productId,
        value.shipmentId
      )
        .then((data) => {
          const responseData = data?.data;
          const product =
            responseData?.tcProductInformation?.[0] || responseData;

          if (product) {
            const netShippingWeight = product.tcProductNetShippingWeight || 0;

            const copiedProduct = {
              ...product,
              productId: null,
              shipmentId: null,
              tcProductArticleNo: product.tcProductArticleNo || "",
              tcProductShipmentDocNo: Array.isArray(product.tcProductShipmentDocNo)
                ? product.tcProductShipmentDocNo
                : product.tcProductShipmentDocNo
                  ? [product.tcProductShipmentDocNo]
                  : [],
              tcProductOrderNo: Array.isArray(product.tcProductOrderNo)
                ? product.tcProductOrderNo.map((no: string) => `${no}`)
                : product.tcProductOrderNo
                  ? [`${product.tcProductOrderNo}`]
                  : [],
              tcProductCertifiedRawMaterialGeographicInformation:
                flattenCertifiedRawMaterialGeographicInformation(
                  product.tcProductCertifiedRawMaterialGeographicInformation || []
                ),
            };

            const mappedStepData = mapProductDataToStepData(
              copiedProduct,
              netShippingWeight
            );

            setStepData(mappedStepData);

            Object.entries(mappedStepData).forEach(([key, val]) => {
              updateField(key as keyof ProductFormData, val);
            });

            setOriginalProductArticleNo("");
            setOriginalShipmentIds([]);

            toggleDrawer();
          } else {
            notify("No product data found", "error");
          }
        })
        .catch((err) => {
          console.error("Error copying product:", err);
          notify(err.message || "Failed to copy product", "error");
        });
    }
  };

  const fetchComponentsForProduct = async (
    targetTcId: string,
    pId: string,
    articleNo: string,
    isCertifiedRM: any
  ) => {
    try {
      const listResp: any = await executeGet(
        TCAppConfig.GET_COMPONENT_DETAILS_LIST_V4,
        { tcId: targetTcId }
      );
      const rawList = listResp?.data?.response ?? listResp?.data;
      let listArr: any[] = [];
      if (typeof rawList === "string") {
        try {
          listArr = JSON.parse(rawList);
        } catch {
          listArr = [];
        }
      } else if (Array.isArray(rawList)) {
        listArr = rawList;
      }
      const mine = listArr.filter((c: any) => {
        const nestedInfo = c?.tcProductComponentInformation?.[0] || {};

        const cProdId = String(c?.productId || nestedInfo?.productId || "").trim().toLowerCase();
        const pProdId = String(pId || "").trim().toLowerCase();
        if (cProdId && pProdId && cProdId === pProdId) return true;

        const cArticle = String(
          c?.tcAssociatedProductArticleNo ||
          c?.tcAssociatedProductNo ||
          c?.associatedProductNo ||
          nestedInfo?.tcAssociatedProductArticleNo ||
          nestedInfo?.tcAssociatedProductNo ||
          nestedInfo?.associatedProductNo ||
          ""
        ).trim().toLowerCase();
        const pArticle = String(articleNo || "").trim().toLowerCase();
        if (pArticle && cArticle && pArticle === cArticle) return true;

        return false;
      });
      if (mine.length === 0) return [];

      // Deduplicate by componentId / componentNo (backend list endpoint returns flat joined rows)
      const uniqueMineMap = new Map<string, any>();
      mine.forEach((c: any) => {
        const nestedInfo = c?.tcProductComponentInformation?.[0] || {};
        const key = String(
          c?.componentId ||
          nestedInfo?.componentId ||
          c?.tcProductComponentNo ||
          c?.tcComponentNumber ||
          c?.componentNo ||
          nestedInfo?.tcProductComponentNo ||
          ""
        ).trim().toLowerCase();
        if (key && !uniqueMineMap.has(key)) {
          uniqueMineMap.set(key, c);
        }
      });
      const uniqueMine = uniqueMineMap.size > 0 ? Array.from(uniqueMineMap.values()) : mine;

      const detailed = await Promise.all(
        uniqueMine.map(async (c: any) => {
          try {
            const dResp: any = await executeGet(
              TCAppConfig.GET_COMPONENT_DETAIL_V4,
              {
                tcId: targetTcId,
                productId: pId,
                componentId: c.componentId,
              }
            );
            const body = dResp?.data ?? dResp;
            const stored = (body?.tcProductComponentInformation || [])[0] || c;
            return { stored, componentId: c.componentId };
          } catch {
            return { stored: c, componentId: c.componentId };
          }
        })
      );
      const real = detailed.filter(
        ({ stored }) => {
          const compNo = String(
            stored?.tcProductComponentNo ||
            stored?.tcComponentNumber ||
            stored?.componentNo ||
            stored?.tcProductComponentDetail ||
            stored?.tcComponentDetail ||
            stored?.componentDetail ||
            ""
          ).trim().toLowerCase();
          return compNo && !["na", "n/a", "not applicable", "none"].includes(compNo);
        }
      );
      const loaded = await Promise.all(
        real.map(({ stored, componentId }) =>
          mapPersistedComponentToInMemory(stored, componentId, isCertifiedRM)
        )
      );
      return loaded;
    } catch (err) {
      console.error("Failed to fetch components:", err);
      return [];
    }
  };

  // Open a saved product for edit. A WITH-component product has separate COMPONENTDETAIL docs
  // linked by productId (the product doc carries no with/without flag and returns an empty
  // tcProductComponentInformation). So we look up the TC's components, filter by this productId,
  // load each one's full detail into the workspace, and open in WITH-component mode (step 1, with
  // the loaded components ready behind "Save and Add Components"). No components → the
  // without-component drawer (unchanged behaviour).
  const openProductEditModal = async (
    editProductId: string,
    editArticleNo: string,
    targetTcId: string = TE_ID!
  ) => {
    const openWithoutComponent = () => {
      setIsWithComponent(false);
      setShowWorkspace(false);
      setComponents([]);
      setDrawerOpen(true);
    };
    try {
      const isCertifiedRM = makeIsCertifiedRM(
        certifiedRawMaterialCodes,
        tcStandardsCache
      );
      const loaded = await fetchComponentsForProduct(
        targetTcId,
        editProductId,
        editArticleNo,
        isCertifiedRM
      );
      if (loaded.length === 0) {
        openWithoutComponent();
        return;
      }
      setComponents(loaded);
      setWithComponentProductId(editProductId);
      setWithComponentArticleNo(editArticleNo);
      setIsWithComponent(true);
      setShowWorkspace(false);
      setActiveStep(0);
      setDrawerOpen(true);
    } catch (err) {
      console.error("Failed to load components for edit:", err);
      openWithoutComponent();
    }
  };

  const onEdit = (row: any) => {
    const value = row?.original;
    const isDraft = !!value.isReturnAutofillDraft;
    setIsEdit(!isDraft);

    const transactionDetails = getTransactionDetailsFormValues() || {};
    const returnTcId = transactionDetails.returnTcId;

    const sourceTcId = isDraft ? returnTcId : TE_ID;

    // Use current shipment from the list if draft, otherwise row's shipmentId
    let targetShipmentId = value.shipmentId;
    let targetShipmentNo =
      value?.tcProductShipmentDocNo && value?.tcProductShipmentDocNo?.length > 0
        ? value?.tcProductShipmentDocNo[0]
        : null;

    if (isDraft) {
      const firstShipment = shipmentData[0];
      if (firstShipment) {
        targetShipmentId = firstShipment.shipmentId;
        targetShipmentNo = firstShipment.tcShipmentInformation?.[0]?.tcShipmentDocNo || null;
      }
    }

    const draftNewProductId = isDraft ? generateProductId() : "";
    setProductId(isDraft ? draftNewProductId : value.productId);
    setShipmentId(targetShipmentId);
    setShipmentNo(targetShipmentNo);

    const processProductData = async (product: any, isDraftVal: boolean, tNo: string | null) => {
      const isReturnTc = isDraftVal || isReturn;

      const rawNetWeight = product.tcProductNetShippingWeight || product.netShippingWeight;
      const netShippingWeight = (isReturnTc && (rawNetWeight === 0 || rawNetWeight === "0" || !rawNetWeight))
        ? ""
        : (rawNetWeight ?? "");

      const rawSuppWeight = product.tcProductSupplementaryWeight;
      const supplementaryWeight = (isReturnTc && (rawSuppWeight === 0 || rawSuppWeight === "0" || !rawSuppWeight))
        ? ""
        : (rawSuppWeight ?? "");

      const rawAdditivesWeight = product.tcProductAdditivesWeight;
      const additivesWeight = (isReturnTc && (rawAdditivesWeight === 0 || rawAdditivesWeight === "0" || !rawAdditivesWeight))
        ? ""
        : (rawAdditivesWeight ?? "");

      const rawUnits = product.tcProductNumberOfUnits;
      const numberOfUnits = (isReturnTc && (rawUnits === 0 || rawUnits === "0" || !rawUnits))
        ? ""
        : (rawUnits?.toString() ?? "");

      // Create mapped step data
      const mappedStepData: ProductFormData = {
        productInfo: {
          // V3 flat API (no pageName) uses tcProductNo; nested (with pageName) uses tcProductArticleNo
          tcProductOrderNo: isDraftVal ? [""] : (
            Array.isArray(product.tcProductOrderNo)
              ? product.tcProductOrderNo
              : product.tcProductOrderNo
                ? [product.tcProductOrderNo]
                : product.tcProductOrder
                  ? [product.tcProductOrder]
                  : [""]
          ),
          tcProductArticleNo: product.tcProductArticleNo || product.tcProductNo || "",
          tcProductProductionDate: isDraftVal ? "" : (product.tcProductProductionDate || product.tcProductionDate || ""),
          tcProductShipmentDocNo: tNo ? [tNo] : [],
          tcProductNumberOfUnits: numberOfUnits,
          tcProductUnitsMetric: product.tcProductUnitsMetric || product.tcProductUnitMetric || "",
          tcProductUnitsMetricOther: product.tcProductUnitsMetricOther || "",
          tcProductNetShippingWeight: netShippingWeight,
          tcProductSupplementaryWeight: supplementaryWeight,
          tcProductInfoSupplementaryWeight:
            isDraftVal ? "" : (product.tcProductInfoSupplementaryWeight || ""),
          tcProductAdditives: isDraftVal ? "" : toAdditivesString(product.tcProductAdditives),
          tcProductAdditivesWeight: additivesWeight,
          tcProductCategoryCode: product.tcProductCategoryCode || "",
          tcProductCategoryUserSpecificTerm:
            product.tcProductCategoryUserSpecificTerm || "",
          tcProductDetailCode: product.tcProductDetailCode || "",
          tcProductDetailUserSpecificTerm:
            product.tcProductDetailUserSpecificTerm || "",
          tcProductCategoryCodeDescription:
            product.tcProductCategoryCodeDescription || "",
          tcProductDetailCodeDescription:
            product.tcProductDetailCodeDescription || "",
        },
        rawMaterialInfo: (
          product.tcProductRawMaterialInformation || []
        ).map((item: any) => {
          const fallbackFromCertWeights =
            (product.tcProductCertifiedWeightInformation || []).find((w: any) => {
              const codeInClaim = String(w.tcProductRawMaterialCodeClaimsCategory || "").toLowerCase();
              const code = String(item.tcProductRawMaterialCode || "").toLowerCase();
              return code && codeInClaim.includes(code);
            })?.tcProductClaimsCategory ||
            (product.tcProductCertifiedWeightInformation || [])[0]?.tcProductClaimsCategory ||
            "";
          const rawCat = item.claimsCategory || item.tcClaimsCategory || item.tcProductRawMaterialCodeClaimsCategory || item.tcProductClaimsCategory || fallbackFromCertWeights || "";
          const catStd = extractProductStandard(rawCat).toLowerCase();
          const activeStdCodes = tcStandardsCache.map((s) => {
            const code = standardCodeFromValue(s).toLowerCase();
            const raw = String(s || "").toLowerCase();
            if (raw.includes("materials matter") || raw.includes("material matter") || raw.includes("mms")) {
              return "mms";
            }
            return code;
          });
          const isSupported = !isReturn || !catStd || catStd === "na" || activeStdCodes.includes(catStd) ||
            (activeStdCodes.includes("mms") && ["ras", "rcs", "grs", "rds", "rms", "rws", "mms"].includes(catStd));
          const effectiveCat = isSupported ? (item.claimsCategory || rawCat || "") : "na";
          const effectiveCertified = isSupported && effectiveCat.toLowerCase() !== "na" ? (item.certified ?? true) : false;

          return {
            tcProductRawMaterialCode: item.tcProductRawMaterialCode || "",
            tcProductRawMaterialPercentage:
              parseFloat(
                item.tcProductRawMaterialPercentage?.toString() || "0"
              ) || 0,
            tcProductRawMaterialUserSpecificTerm:
              item.tcProductRawMaterialUserSpecificTerm || "",
            tcProductRawMaterialCodeDescription:
              item.tcProductRawMaterialCodeDescription || "",
            claimsCategory: effectiveCat,
            certified: effectiveCertified,
          };
        }),
        geographicInfo: isDraftVal ? [] : flattenCertifiedRawMaterialGeographicInformation(
          product.tcProductCertifiedRawMaterialGeographicInformation || []
        ),
        productCertifiedWeightInfo: isDraftVal ? [] : (product.tcProductCertifiedWeightInformation || []).map((item: any) => ({
          ...item,
          tcProductCertifiedWeight: item.tcProductClaimsCategoryCertifiedWeight ?? item.tcProductCertifiedWeight ?? 0,
          tcProductClaimsCategoryCertifiedWeight: item.tcProductClaimsCategoryCertifiedWeight ?? item.tcProductCertifiedWeight ?? 0,
        })),
        productFacilitiesInfo: isDraftVal ? [] : normalizeProductFacilityInformation(
          product.tcProductFacilityInformation || product.tcProductFacilitiesInformation || []
        ),
        productFirstProcessorInfo: isDraftVal ? [] : (product.tcProductFirstProcessorInformation || []).map((item: any) => ({
          tcProductFirstProcessorTeId: item.tcProductFirstProcessorTeId || "",
          tcProductFirstProcessCode: Array.isArray(item.tcProductFirstProcessCode)
            ? item.tcProductFirstProcessCode
            : item.tcProductFirstProcessCode ? [item.tcProductFirstProcessCode] : []
        })),
        additionalInfo: {
          tcProductInfoAdditional: isDraftVal ? "" : (product.tcProductInfoAdditional || ""),
          tcProductInfoFabricWeightAndConstruct:
            isDraftVal ? "" : (product.tcProductInfoFabricWeightAndConstruct || ""),
          tcProductInfoFabricWeightOfFinishedProduct:
            isDraftVal ? "" : (product.tcProductInfoFabricWeightOfFinishedProduct || ""),
          tcProductInfoFiberLength:
            isDraftVal ? "" : (product.tcProductInfoFiberLength || ""),
          tcProductInfoYarnCount: isDraftVal ? "" : (product.tcProductInfoYarnCount || ""),
          tcProductAdditives: isDraftVal ? "" : toAdditivesString(product.tcProductAdditives),
          tcProductAdditivesWeight: isDraftVal ? 0 : (product.tcProductAdditivesWeight || 0),
          tcProductFeedstock: isDraftVal ? [] : (Array.isArray(product.tcProductFeedstock)
            ? product.tcProductFeedstock
            : product.tcProductFeedstock ? [product.tcProductFeedstock] : []),
          tcProductPercentTextileWaste: isDraftVal ? "" : (product.tcProductPercentTextileWaste || ""),
          tcProductPackagingFor: isDraftVal ? [] : normalizeStringArray(product.tcProductPackagingFor),
          tcProductGtin: isDraftVal ? "" : (product.tcProductGtin || ""),
          tcProductReclaimedMaterialSource: isDraftVal ? [] : (Array.isArray(product.tcProductReclaimedMaterialSource)
            ? product.tcProductReclaimedMaterialSource
            : product.tcProductReclaimedMaterialSource ? [product.tcProductReclaimedMaterialSource] : []),
          tcProductInfoTechnicalSpecifications:
            isDraftVal ? "" : (product.tcProductInfoTechnicalSpecifications || ""),
          tcProductOriginOrganizationTeId: isDraftVal ? [] : (Array.isArray(product.tcProductOriginOrganizationTeId)
            ? product.tcProductOriginOrganizationTeId
            : product.tcProductOriginOrganizationTeId ? [product.tcProductOriginOrganizationTeId] : []),
          tcProductOriginFacilityTeId: isDraftVal ? [] : (Array.isArray(product.tcProductOriginFacilityTeId)
            ? product.tcProductOriginFacilityTeId
            : product.tcProductOriginFacilityTeId ? [product.tcProductOriginFacilityTeId] : []),
        },
      };

      console.log("Mapped step data:", mappedStepData);

      setStepData(mappedStepData);

      // Update form fields
      Object.entries(mappedStepData).forEach(([key, val]) => {
        updateField(key as keyof ProductFormData, val);
      });

      // Set originals for change detection in scenarios
      setOriginalProductArticleNo(isDraftVal ? "" : (product.tcProductArticleNo || ""));
      setOriginalShipmentIds(isDraftVal ? [] : [value.shipmentId]);

      // Route to edit modal
      if (isDraftVal) {
        const compsList = product.tcProductComponentInformation || product.components || [];
        const hasInlineComponents = Array.isArray(compsList) &&
          compsList.length > 0 &&
          compsList.some(
            (c: any) => {
              const compNo = String(
                c?.tcProductComponentNo ||
                c?.tcComponentNumber ||
                c?.componentNo ||
                c?.tcProductComponentDetail ||
                c?.tcComponentDetail ||
                c?.componentDetail ||
                ""
              ).trim().toLowerCase();
              return compNo && !["na", "n/a", "not applicable", "none"].includes(compNo);
            }
          );
        const isCertifiedRM = makeIsCertifiedRM(
          certifiedRawMaterialCodes,
          tcStandardsCache
        );
        const isSourceV3 = String(transactionDetails.returnTcSourceVersion || "").toUpperCase() === "V3";
        let loaded: any[] = [];
        if (hasInlineComponents) {
          loaded = compsList.map((c: any) =>
            mapPersistedComponentToInMemory(c, c.componentId || "", isCertifiedRM)
          );
        } else if (!isSourceV3 && returnTcId && product.productId) {
          // V4 Return TC: components are not nested in the product payload;
          // fetch them from the component-list API using the returnTcId.
          loaded = await fetchComponentsForProduct(
            returnTcId,
            product.productId,
            product.tcProductArticleNo || "",
            isCertifiedRM
          );
        }
        if (loaded.length > 0) {
          setComponents(loaded);
          setWithComponentProductId(draftNewProductId);
          setWithComponentArticleNo(product.tcProductArticleNo || "");
          setIsWithComponent(true);
          setShowWorkspace(false);
          setActiveStep(0);
          setDrawerOpen(true);
        } else {
          setIsWithComponent(false);
          setShowWorkspace(false);
          setComponents([]);
          setDrawerOpen(true);
        }
      } else {
        void openProductEditModal(
          value.productId,
          product.tcProductArticleNo || ""
        );
      }
    };

    if (isDraft) {
      const returnProducts = useTransactionCertificateStore.getState().returnTcProducts || [];
      console.log("[onEdit] isDraft true. value:", value);
      console.log("[onEdit] returnProducts cache:", returnProducts);
      const product = returnProducts.find(
        (p: any) => p.productId === value.productId || p.tcProductArticleNo === value.tcProductArticleNo
      );
      console.log("[onEdit] Found product:", product);

      const fetchAndProcess = async () => {
        try {
          const isSourceV3 = String(transactionDetails.returnTcSourceVersion || "").toUpperCase() === "V3";
          const data = await fetchProductaddDetails(
            executeGet,
            returnTcId,
            value.productId,
            value.shipmentId,
            isSourceV3
          );
          const payload = data?.data?.response || data?.data;
          const freshProduct = payload?.tcProductInformation?.[0] || payload;
          if (freshProduct) {
            // Update cache in Zustand
            const exists = returnProducts.some((p: any) => p.productId === value.productId);
            const updatedProducts = exists
              ? returnProducts.map((p: any) => 
                  (p.productId === value.productId || p.tcProductArticleNo === value.tcProductArticleNo)
                    ? { ...p, ...freshProduct, productId: value.productId }
                    : p
                )
              : [...returnProducts, { ...freshProduct, productId: value.productId }];
            useTransactionCertificateStore.getState().setReturnTcProducts?.(updatedProducts);
            await processProductData({ ...product, ...freshProduct }, true, targetShipmentNo);
          } else {
            if (product) {
              await processProductData(product, true, targetShipmentNo);
            } else {
              notify("No product data found in return products cache or API", "error");
            }
          }
        } catch (err: any) {
          console.error("Failed to fetch fresh product details on edit:", err);
          if (product) {
            await processProductData(product, true, targetShipmentNo);
          } else {
            notify(err.message || "Failed to load product details", "error");
          }
        }
      };

      if (product && (product.tcProductCategoryCode || product.tcProductCategoryCodeDescription)) {
        void processProductData(product, true, targetShipmentNo);
      } else {
        void fetchAndProcess();
      }
    } else if (sourceTcId) {
      const isSourceV3 = String(transactionDetails.returnTcSourceVersion || "").toUpperCase() === "V3";
      fetchProductaddDetails(
        executeGet,
        sourceTcId,
        value.productId,
        value.shipmentId,
        isSourceV3
      )
        .then((data) => {
          console.log("Full API Response:", data);

          // Extract the product from the nested structure
          const responseData = data?.data;
          const product =
            responseData?.tcProductInformation?.[0] || responseData;

          console.log("Extracted product data:", product);

          if (product) {
            void processProductData(product, false, targetShipmentNo);
          } else {
            notify("No product data found", "error");
          }
        })
        .catch((err) => {
          console.error("Error fetching product details:", err);
          notify(err.message || "Failed to load product details", "error");
        });
    }
  };

  const onDelete = (row: any) => {
    const productArticleNo = row?.original?.tcProductArticleNo;
    const productId = row?.original?.productId;
    const shipmentId = row?.original?.shipmentId;
    const shipmentNo = row?.original?.tcProductShipmentDocNo?.[0] ?? null;

    setProductId(productId);
    setShipmentId(shipmentId);
    setShipmentNo(shipmentNo);
    setTcProductArticleNo(productArticleNo);
    setDialogOpen(true);
    setPopUpBtnName("Delete");
    setPopUpTitle("Confirm Delete");
    setPopUpMessage(
      `Deleting Product ${productArticleNo} will remove all associated records. Are you sure you want to delete it?`
    );
  };

  const handleConfirmSave = async () => {
    try {
      const targetProduct = tableData.find((p) => p.tcProductArticleNo === tcProductArticleNo);
      const isDraft = !!(targetProduct as any)?.isReturnAutofillDraft;

      const store = useTransactionCertificateStore.getState();
      const currentReturnProducts = store.returnTcProducts || returnTcProducts || [];
      const deletedArticleNo = String(tcProductArticleNo || "").trim().toLowerCase();
      const tcKey = String(TE_ID || (currentReturnProducts[0] as any)?.tcId || "").trim();

      if (deletedArticleNo && tcKey) {
        const currentMap = store.deletedReturnTcArticlesMap || {};
        const currentList = currentMap[tcKey] || [];
        if (!currentList.includes(deletedArticleNo)) {
          const updatedMap = { ...currentMap, [tcKey]: [...currentList, deletedArticleNo] };
          store.setDeletedReturnTcArticlesMap?.(updatedMap);
        }
        try {
          const key = `return_tc_deleted_${tcKey}`;
          const storedKeyData: string[] = JSON.parse(localStorage.getItem(key) || "[]");
          if (!storedKeyData.includes(deletedArticleNo)) {
            storedKeyData.push(deletedArticleNo);
            localStorage.setItem(key, JSON.stringify(storedKeyData));
          }
        } catch (e) {
          // ignore
        }
      }

      const updatedProducts = currentReturnProducts.filter(
        (p: any) =>
          String(p.tcProductArticleNo || p.tcProductNo || "").trim().toLowerCase() !== deletedArticleNo
      );

      if (isDraft) {
        store.setReturnTcProducts?.(updatedProducts);
        setDialogOpen(false);
        resetFormData();
        await fetchProductDetails();
        notify("Successfully deleted", "success");
        return;
      }

      if (!productId) {
        notify("No product selected for deletion", "error");
        return;
      }

      const res = await executeDelete(`${TCAppConfig.TC_DELETE_API_V4}`, {
        tcId: TE_ID,
        shipmentId: shipmentId,
        productId: productId,
        productArticleNo: tcProductArticleNo,
      });

      const isDeleteSuccess =
        res?.status === 200 ||
        res?.status === 204 ||
        (res as any)?.data?.success === true ||
        (res as any)?.success === true ||
        (res && !(res as any).error && (res as any).status !== "error");

      if (isDeleteSuccess) {
        store.setReturnTcProducts?.(updatedProducts);
        setDialogOpen(false);
        resetFormData();
        await fetchProductDetails();
        notify("Successfully deleted", "success");
      }
    } catch (error) {
      console.error("Error in handleConfirmSave:", error);
      notify("Failed to delete the product", "error");
    }
  };

  const handleCancelSave = () => {
    setDialogOpen(false);
    if (drawerOpen) {
      toggleDrawer();
    }
  };

  // ===== TABLE CONFIGURATION =====
  // Landing table columns per the "Certified Products" wireframe (slide 1):
  // Article No, Net Shipping Weight, Supplementary Weight, (derived) Certified Weight,
  // (derived) Non-Certified Weight. Full product details remain in the expandable row panel.
  const weightCell = ({ cell, row }: any) => {
    if (row?.original?.isReturnAutofillDraft) {
      return (
        <span style={{ 
          color: '#ef4444', 
          backgroundColor: '#fee2e2', 
          padding: '2px 8px', 
          borderRadius: '12px', 
          fontSize: '0.75rem', 
          fontWeight: 600,
          border: '1px solid #fca5a5'
        }}>
          Review needed
        </span>
      );
    }
    const v = cell.getValue();
    if (v === undefined || v === null || v === "") return <div></div>;
    return <div>{formatTcV4Decimal(v)}</div>;
  };
  // Returns a row-background-color tint for products that have RMDF violations,
  // mirroring the amber pastel used in SC v4 facility-details for warning rows.
  const getRowBgColor = (row: any) => {
    if (row?.original?.isReturnAutofillDraft) {
      return "#FFF2F2"; // Light pink warning color for return TC drafts
    }
    const articleNo: string = row?.original?.tcProductArticleNo || "";
    return rmdfViolations[articleNo] ? "#FFF8E7" : undefined;
  };

  const columns: MRT_ColumnDef<any>[] = [
    {
      accessorKey: "tcProductArticleNo",
      header: "Product Article Number",
      minSize: 150,
      maxSize: 250,
    },
    {
      accessorKey: "tcProductNetShippingWeight",
      header: "Net Shipping Weight (kg)",
      minSize: 120,
      maxSize: 200,
      Cell: weightCell,
    },
    {
      accessorKey: "tcProductSupplementaryWeight",
      header: "Supplementary Weight (kg)",
      minSize: 120,
      maxSize: 200,
      Cell: weightCell,
    },
    {
      accessorKey: "tcProductCertifiedWeight",
      header: "Product Certified Weight (kg)",
      minSize: 120,
      maxSize: 220,
      Cell: weightCell,
    },
    {
      accessorKey: "tcProductNonCertifiedWeight",
      header: "Product Non-Certified Weight (kg)",
      minSize: 130,
      maxSize: 240,
      Cell: weightCell,
    },
  ];

  // ===== STEPPER CONFIGURATION =====
  const steps = [
    {
      label: "Product Information",
      content: (
        <ProductInfo
          formRef={productInfoRef}
          productData={stepData.productInfo}
          setProductData={(data) => handleDataUpdate("productInfo", data)}
          shipmentId={shipmentId || ""}
          withComponent={isWithComponent}
        />
      ),
    },
    {
      label: "Product Raw Material Info",
      content: (
        <>
          {isWithComponent && (
            <ProductTotalsSummary
              components={components}
              productSupplementaryWeight={
                Number(stepData.productInfo.tcProductSupplementaryWeight) || 0
              }
            />
          )}
          <RawMaterialInfo
            formRef={rawMaterialInfoRef}
            rawMaterialInfoData={stepData.rawMaterialInfo}
            setRawMaterialInfoData={(data) =>
              handleDataUpdate("rawMaterialInfo", data)
            }
            productData={stepData.productInfo}
            tcStandards={tcStandardsCache}
            onValidationChange={(isValid) => setIsRawMaterialValid(isValid)}
            // WITH-component: the product RM table is auto-derived (deduped across components);
            // the user may only edit the user-specific term + claims category.
            readOnlyDerived={isWithComponent}
          />
        </>
      ),
    },
    {
      label: "Product Certified Weight Info",
      content: (
        <ProductCertifiedWeight
          formRef={productCertifiedWeightInfoRef}
          productCertifiedWeightInfoData={stepData.productCertifiedWeightInfo}
          setProductCertifiedWeightInfoData={(data: any) =>
            handleDataUpdate("productCertifiedWeightInfo", data)
          }
          productData={stepData.productInfo}
          rawMaterialInfoData={stepData.rawMaterialInfo}
          tcStandards={tcStandardsCache}
        />
      ),
    },
    {
      label: "Product Certified Raw Material Geographic Info",
      content: (
        <ProductGeoInfo
          formRef={geographicInfoRef}
          productGeoData={
            Array.isArray(stepData.geographicInfo)
              ? stepData.geographicInfo
              : []
          }
          setProductGeoData={(data) => handleDataUpdate("geographicInfo", data)}
          productData={stepData.productInfo}
          rawMaterialInfoData={stepData.rawMaterialInfo}
          tcStandards={tcStandardsCache}
        />
      ),
    },
    {
      label: "Product First Processor and Facility Info",
      content: (
        <Box display="flex" flexDirection="column" gap={3} width="100%">
          <ProductFirstProcessorInfo
            formRef={productFirstProcessorInfoRef}
            ProductFirstProcessData={stepData.productFirstProcessorInfo}
            setProductFirstProcessInfoData={(data) =>
              handleDataUpdate("productFirstProcessorInfo", data)
            }
          />
          <ProductFacilitiesInfo
            formRef={productFacilitiesInfoRef}
            ProductFacilitiesData={stepData.productFacilitiesInfo}
            setProductFacilitiesInfoData={(data) =>
              handleDataUpdate("productFacilitiesInfo", data)
            }
          />
        </Box>
      ),
    },
    {
      label: "Product Additional Info",
      content: (
        <AdditionalInfo
          formRef={additionalInfoRef}
          additionalInfoData={stepData.additionalInfo}
          setAdditionalInfoData={(data) =>
            handleDataUpdate("additionalInfo", data)
          }
          productCategoryCode={stepData.productInfo.tcProductCategoryCode}
          productDetailCode={stepData.productInfo.tcProductDetailCode}
          isWithComponent={isWithComponent}
        />
      ),
    },
  ];

  // Primary (right-hand) modal button. WITH-component replaces Step-1 "Next" with
  // "Save and Add Components" (saves the product info, then opens the workspace).
  const isLastStep = activeStep === steps.length - 1;
  const primaryLabel =
    isWithComponent && activeStep === 0
      ? "Save and Add Components"
      : isLastStep
      ? "Save"
      : "Next";
  const primaryOnClick =
    isWithComponent && activeStep === 0
      ? handleSaveProductInfoOnly
      : isLastStep
      ? handleFinish
      : handleNext;

  const isNextDisabled = useMemo(() => {
    if ([1, 2, 3, 4].includes(activeStep)) {
      if (isRestrictPopUpSave) return true;
      if (activeStep === 4) {
        const fpCount = (stepData.productFirstProcessorInfo || []).length;
        const facCount = (stepData.productFacilitiesInfo || []).length;
        return fpCount === 0 || facCount === 0;
      }
    }
    return false;
  }, [activeStep, isRestrictPopUpSave, stepData.productFirstProcessorInfo, stepData.productFacilitiesInfo]);

  const actions: ActionButtonConfig[] = [
    ...(activeStep !== 0
      ? [
        {
          label: "Back",
          onClick: handleBack,
          variant: "contained" as "contained",
          color: "default" as "default",
          disabled: [1, 2, 3, 4].includes(activeStep)
            ? isRestrictPopUpSave
            : false,
        },
      ]
      : []),
    {
      label: primaryLabel,
      onClick: primaryOnClick,
      variant: "contained" as "contained",
      color: "primary" as "primary",
      disabled: isNextDisabled,
      isLoading: (nextLoader || loading) && activeStep === 0,
    },
  ];

  // ===== INITIALIZATION =====
  useEffect(() => {
    const initializeComponent = async () => {
      if (TE_ID) {
        // Transaction Details already loaded the TC standards. Reuse its form
        try {
          const transactionDetails = getTransactionDetailsFormValues() || {};

          const rmResp = await executeGet(
            TCAppConfig.GET_CERTIFIED_RAW_MATERIAL_CODES
          );
          const rmList = rmResp?.data?.response || rmResp?.data || [];
          setCertifiedRawMaterialCodes(Array.isArray(rmList) ? rmList : []);
        } catch (err) {
          console.error("Failed to initialize product validation data:", err);
        }

        // The pagination effect owns product-list loading. Calling it here as
        // well issued the same list/ref-code requests twice on every entry.
        await fetchShipmentData();
      } else {
        updateTableData([]);
      }
      resetFormValueByPage();
      setRestrictPopUpSave(false);
    };

    initializeComponent();
  }, [TE_ID]);

  // Fetch incoming TCs list to validate RMDF feedstock and source requirements
  // dynamically inside the Product Information tab.
  useEffect(() => {
    const loadIncomingTcsForValidation = async () => {
      if (!TE_ID) return;
      try {
        const data = await fetchIncomingTcDetailsList(executeGet, TE_ID, 100, 0);
        setIncomingTcs(Array.isArray(data?.entries) ? data.entries : []);
      } catch (err) {
        console.error("Failed to load incoming TCs for validation:", err);
      } finally {
        setHasFetchedIncomingTcs(true);
      }
    };
    loadIncomingTcsForValidation();
  }, [TE_ID]);

  // Fetch detailed product information (to obtain feedstock & reclaimed source values)
  // whenever the product list (tableData) changes, as list endpoints do not return these fields.
  useEffect(() => {
    const loadDetailedProducts = async () => {
      if (!TE_ID || !tableData.length) {
        setDetailedProducts([]);
        setHasFetchedDetailedProducts(true);
        return;
      }
      try {
        const details = await Promise.all(
          tableData.map(async (p: any) => {
            if (p.isReturnAutofillDraft) {
              const match = (returnTcProducts || []).find(
                (retP: any) => retP.productId === p.productId || retP.tcProductArticleNo === p.tcProductArticleNo
              );
              if (match) {
                return { ...p, ...match };
              }
            }
            try {
              const res = await fetchProductaddDetails(executeGet, TE_ID, p.productId, p.shipmentId);
              const prod = res?.data?.tcProductInformation?.[0] || res?.data || {};
              return { ...p, ...prod };
            } catch (err) {
              console.error("Failed to load details for product:", p.productId, err);
              return p;
            }
          })
        );
        setDetailedProducts(details);
      } catch (err) {
        console.error("Error loading detailed products:", err);
      } finally {
        setHasFetchedDetailedProducts(true);
      }
    };
    loadDetailedProducts();
  }, [tableData, TE_ID, returnTcProducts]);

  // Dynamically compute and store RMDF violations whenever the detailed product list (detailedProducts)
  // or incoming TCs (incomingTcs) change. This instantly updates the Zustand tab error flags,
  // resolving the deadlock when the user configures the missing feedstock/source values.
  useEffect(() => {
    // Only run this compute logic and overwrite the store once the initial API loads have finished.
    if (!hasFetchedIncomingTcs || !hasFetchedDetailedProducts) {
      return;
    }

    const violations: Record<string, { missingFeedstock: boolean; missingReclaimedSource: boolean }> = {};
    
    if (incomingTcs.length > 0 && detailedProducts.length > 0) {
      incomingTcs.forEach((item: any) => {
        const incomingTcInfo = item.incomingTcInfo || {};
        const isRMDF = Boolean(incomingTcInfo.tcIncomingRMDFNo || (incomingTcInfo as any).tcIncomingType === "RMDF");
        if (!isRMDF) return;

        const articleNo = incomingTcInfo.tcAssociatedProductArticleNo;
        if (!articleNo) return;

        const product = detailedProducts.find((p) => String((p as any).tcProductArticleNo) === String(articleNo)) as any;
        if (!product) return;

        const feedstock = product.tcProductFeedstock;
        const reclaimedSource = product.tcProductReclaimedMaterialSource;

        const missingFeedstock = !feedstock || (Array.isArray(feedstock) ? feedstock.length === 0 : String(feedstock).trim() === "");
        const missingReclaimedSource = !reclaimedSource || (Array.isArray(reclaimedSource) ? reclaimedSource.length === 0 : String(reclaimedSource).trim() === "");

        if (missingFeedstock || missingReclaimedSource) {
          violations[articleNo] = {
            missingFeedstock,
            missingReclaimedSource,
          };
        }
      });
    }

    const hasDraft = tableData.some((row: any) => row.isReturnAutofillDraft);
    const hasError = ["W", "WITHDRAWN", "E", "EXPIRED", "S", "SUSPENDED"].includes(sellerScStatus) ? false : (Object.keys(violations).length > 0 || hasDraft);
    useTcTabErrorStore.getState().setTabError('product-details', hasError);
    useTcTabErrorStore.getState().setRmdfViolations(violations);
  }, [detailedProducts, incomingTcs, hasFetchedIncomingTcs, hasFetchedDetailedProducts, tableData]);

  // ===== NAVIGATION HANDLERS =====
  const validateTcClaimsCategoryCoverage = async (): Promise<boolean> => {
    let standards = tcStandardsCache;
    if (!standards || standards.length === 0) {
      const transactionDetails = getTransactionDetailsFormValues() || {};
      const standardValue = transactionDetails.tcStandard;
      standards = Array.isArray(standardValue)
        ? standardValue
        : standardValue
        ? [String(standardValue)]
        : [];
    }
    const tcStandardCodes = Array.from(
      new Set(standards.map(standardCodeFromValue).filter(Boolean))
    );
    if (!TE_ID || tcStandardCodes.length === 0) {
      notify(
        "Unable to validate Product Claims Categories because TC standards are unavailable",
        "error"
      );
      return false;
    }

    try {
      // The product LIST rows don't carry the claims fields, so derive the
      // claims set from the consolidated certified-weight summary (grouped
      // from the saved product docs). Fall back to the list-derived claims
      // when the summary has no rows.
      let claimsCategories: string[] = [];
      const consolidated: any = await executeGet(
        TCAppConfig.GET_CERTIFIED_WEIGHT_INFO_CONSOLIDATED_V4,
        { tcId: TE_ID }
      );
      if (consolidated?.status === 200) {
        claimsCategories = Array.from(
          new Set(
            certifiedWeightRowsFromResponseData(consolidated.data)
              .map((row: any) => certifiedWeightClaimFromRow(row))
              .filter(Boolean)
          )
        );
      }
      if (claimsCategories.length === 0) {
        // Validate the complete TC rather than only the current table page.
        const response: any = await executeGet(
          TCAppConfig.GET_LIST_PRODUCT_INFO_V4,
          {
            tcId: TE_ID,
            limit: 100, // MAX_PRODUCTS_ALLOWED fallback
            offset: 0,
          }
        );
        if (!response || response.status !== 200) {
          notify("Unable to validate Product Claims Categories", "error");
          return false;
        }

        let listProducts: any[] = [];
        if (response?.data?.response) {
          if (typeof response.data.response === "string") {
            try {
              listProducts = JSON.parse(response.data.response);
            } catch (e) {
              listProducts = [];
            }
          } else if (Array.isArray(response.data.response)) {
            listProducts = response.data.response;
          }
        }

        if (listProducts.length === 0) {
          notify("At least one product is required", "error");
          return false;
        }

        // Fetch the full details for each product in the list to populate nested arrays
        const products = await Promise.all(
          listProducts.map(async (p: any) => {
            try {
              const detailRes = await fetchProductaddDetails(
                executeGet,
                TE_ID,
                p.productId,
                p.shipmentId
              );
              return (
                detailRes?.data?.tcProductInformation?.[0] ||
                detailRes?.data ||
                p
              );
            } catch (err) {
              console.error(
                "Failed to fetch details for product:",
                p.productId,
                err
              );
              return p;
            }
          })
        );

        const getProductClaimsCategories = (prod: any): string[] => {
          const info = Array.isArray(prod?.tcProductCertifiedWeightInformation)
            ? prod.tcProductCertifiedWeightInformation
            : [];
          return info
            .map(
              (w: any) =>
                w.tcProductClaimsCategory ||
                w.claimsCategory ||
                w.tcClaimsCategory ||
                w.tcStandard ||
                w.tcProductRawMaterialCodeClaimsCategory ||
                ""
            )
            .filter(Boolean);
        };

        claimsCategories = Array.from(
          new Set(products.flatMap(getProductClaimsCategories))
        );
      }
      const selectedStandards = new Set(tcStandardCodes);
      if (selectedStandards.has("MMS")) {
        ["RAS", "RCS", "GRS", "RMS", "RWS"].forEach((std) => {
          selectedStandards.add(std);
        });
      }
      const invalidClaim = claimsCategories.find(
        (category) => !selectedStandards.has(standardCodeFromValue(category))
      );
      if (invalidClaim) {
        notify(
          `Product Claims Category ${invalidClaim} does not belong to a selected TC standard`,
          "error"
        );
        return false;
      }

      const coveredStandards = new Set(
        claimsCategories.map(standardCodeFromValue).filter(Boolean)
      );
      const missingStandards = tcStandardCodes.filter(
        (standard) => !coveredStandards.has(standard)
      );
      if (missingStandards.length > 0) {
        notify(
          `At least one Product Claims Category is required for each TC standard. Missing: ${missingStandards.join(", ")}`,
          "error"
        );
        return false;
      }

      // Validate combination rules for all products on this TC
      const productsToValidate = detailedProducts.length > 0 ? detailedProducts : [];
      for (const p of productsToValidate) {
        const isCertifiedRM = makeIsCertifiedRM(
          certifiedRawMaterialCodes,
          tcStandardsCache
        );
        const comps =
          Array.isArray(p.tcProductComponentInformation) &&
          p.tcProductComponentInformation.length > 0 &&
          p.tcProductComponentInformation[0]?.tcProductComponentNo !== "na"
            ? p.tcProductComponentInformation
            : await fetchComponentsForProduct(
                TE_ID,
                p.productId || "",
                p.tcProductArticleNo || p.tcProductNo || "",
                isCertifiedRM
              );

        const validation = validateProductClaimsCombinations({
          isWithComponent: comps.length > 0,
          rawMaterials:
            p.tcProductRawMaterialInformation || p.rawMaterialInfo || [],
          certifiedWeights:
            p.tcProductCertifiedWeightInformation ||
            p.productCertifiedWeightInfo ||
            [],
          components: comps,
          articleNo: p.tcProductArticleNo || p.tcProductNo,
        });
        if (!validation.isValid && validation.errorMessage) {
          notify(validation.errorMessage, "error");
          return false;
        }
      }

      return true;
    } catch (error) {
      console.error("Failed to validate TC Product Claims Categories:", error);
      notify("Unable to validate Product Claims Categories", "error");
      return false;
    }
  };

  const onNext = async () => {
    if (!["W", "WITHDRAWN", "E", "EXPIRED", "S", "SUSPENDED"].includes(sellerScStatus)) {
      if (!(await validateTcClaimsCategoryCoverage())) return;
    }
    // Component Information is merged into Product Information; the next tab is now Incoming TC.
    navigate(`/v4/tc/${formtype}/incoming-tc-details/${TE_ID}`);
  };

  const onBack = () => {
    navigate(`/v4/tc/${formtype}/shipment-details/${TE_ID}`);
  };

  // ===== RENDER =====
  if (isInitialLoading) {
    return <LoaderComponent />;
  }

  return (
    <>
      {/* ── Return TC Disclaimer Banner ── */}
      {getTransactionDetailsFormValues()?.tcIsReturn === "Yes" &&
        tableData.some((row: any) => row.isReturnAutofillDraft) && (
          <Box mb={2} sx={{
            display: "flex",
            alignItems: "center",
            bgcolor: "#e5f6fd",
            border: "1px solid #0288d1",
            borderRadius: "4px",
            px: 2,
            py: 1.5,
          }}>
            <Typography variant="body2" sx={{ color: "#01579b", fontWeight: 500 }}>
              Please edit the product information or delete the products given below from the return TC this will be for return tc only
            </Typography>
          </Box>
        )}

      {/* ── RMDF Violation Banner ── mirrors SC v4 facility-details advisory bar ── */}
      {Object.keys(rmdfViolations).length > 0 && (
        <Box mb={1} sx={{
          display: "flex", alignItems: "center", flexWrap: "wrap", gap: 1,
          bgcolor: "#fffbf0",
          border: "1px solid #e8a000",
          borderRadius: "4px",
          px: 1.5, py: 0.75,
        }}>
          <Typography variant="caption" sx={{ color: "#7a4f00", fontWeight: 700, mr: 0.5 }}>
            ⚠ Product Details Need Attention ({Object.keys(rmdfViolations).length}):
          </Typography>
          {Object.entries(rmdfViolations).flatMap(([articleNo, v], i) => {
            const chips: React.ReactNode[] = [];

            if (v.missingFeedstock) {
              chips.push(
                <Tooltip
                  key={`${articleNo}-feedstock`}
                  arrow
                  title={
                    <Box sx={{ p: 0.5 }}>
                      <Typography variant="caption" sx={{ fontWeight: 700, display: "block" }}>
                        Product: {articleNo}
                      </Typography>
                      <Typography variant="caption" sx={{ display: "block", mt: 0.5 }}>
                        Reclaimed material details are required because the linked incoming
                        certificate is an RMDF.
                      </Typography>
                      <Typography variant="caption" sx={{ display: "block", mt: 0.5 }}>
                        Missing: <strong style={{ color: "#ffb300" }}>Product Feedstock</strong>
                      </Typography>
                      <Typography variant="caption" sx={{ display: "block", mt: 1, fontStyle: "italic", color: "#ffb300" }}>
                        Click edit on the highlighted row below to fix this.
                      </Typography>
                    </Box>
                  }
                >
                  <Chip
                    label={`Product ${articleNo} — Missing: Product Feedstock`}
                    size="small"
                    sx={{
                      bgcolor: "#ffecb3",
                      color: "#7a4f00",
                      fontWeight: 600,
                      fontSize: "0.7rem",
                      height: 20,
                      cursor: "help",
                      border: "1px solid #ffb300",
                    }}
                  />
                </Tooltip>
              );
            }

            if (v.missingReclaimedSource) {
              chips.push(
                <Tooltip
                  key={`${articleNo}-reclaimed`}
                  arrow
                  title={
                    <Box sx={{ p: 0.5 }}>
                      <Typography variant="caption" sx={{ fontWeight: 700, display: "block" }}>
                        Product: {articleNo}
                      </Typography>
                      <Typography variant="caption" sx={{ display: "block", mt: 0.5 }}>
                        Reclaimed material details are required because the linked incoming
                        certificate is an RMDF.
                      </Typography>
                      <Typography variant="caption" sx={{ display: "block", mt: 0.5 }}>
                        Missing: <strong style={{ color: "#ffb300" }}>Product Material Reclaimed Source</strong>
                      </Typography>
                      <Typography variant="caption" sx={{ display: "block", mt: 1, fontStyle: "italic", color: "#ffb300" }}>
                        Click edit on the highlighted row below to fix this.
                      </Typography>
                    </Box>
                  }
                >
                  <Chip
                    label={`Product ${articleNo} — Missing: Product Material Reclaimed Source`}
                    size="small"
                    sx={{
                      bgcolor: "#ffecb3",
                      color: "#7a4f00",
                      fontWeight: 600,
                      fontSize: "0.7rem",
                      height: 20,
                      cursor: "help",
                      border: "1px solid #ffb300",
                    }}
                  />
                </Tooltip>
              );
            }

            return chips;
          })}
          <Typography variant="caption" sx={{ color: "#7a4100", ml: 0.5 }}>
            Hover over each chip for details, or click the row edit button below.
          </Typography>
        </Box>
      )}

      {/* Add Product Button */}
      <Box display="flex" mb={3} justifyContent="flex-end" width="100%" gap={1}>
        {!hasSubmitTc && !isReturn && (
          <>
            <Tooltip
              title={rowCount >= MAX_PRODUCTS_ALLOWED ? PRODUCT_LIMIT_ERROR : ""}
            >
              <span>
                <StyledButton
                  variant="contained"
                  sx={{ boxShadow: "none" }}
                  onClick={onAdd}
                >
                  Add Product Without Component
                </StyledButton>
              </span>
            </Tooltip>
            <Tooltip
              title={rowCount >= MAX_PRODUCTS_ALLOWED ? PRODUCT_LIMIT_ERROR : ""}
            >
              <span>
                <StyledButton
                  variant="outlined"
                  sx={{ boxShadow: "none" }}
                  onClick={onAddWithComponent}
                >
                  Add Product With Component
                </StyledButton>
              </span>
            </Tooltip>
          </>
        )}
      </Box>

      {/* Products Table */}
      <Box display="grid">
        <CustomTable
          columns={columns}
          data={tableData}
          enableTopToolbar={false}
          enableBottomToolbar={true}
          enableRowActions={!hasSubmitTc}
          rowCount={rowCount}
          paginations={pagination}
          setPaginations={setPagination}
          getRowBackgroundColor={getRowBgColor}
          renderDetailPanel={({ row }) =>
            row.getIsExpanded() ? (
              <RenderDetailPanel row={row} />
            ) : (
              <div>No data available</div>
            )
          }
          renderRowActions={({ row }) => (
            <ActionsMenu
              row={row}
              onEdit={onEdit}
              onCopy={onCopy}
              onDelete={onDelete}
            />
          )}
        />
      </Box>

      {/* Product Form Modal. WITH-component: the Component Creation Workspace replaces the stepper
          (full-width, no footer actions — it carries its own buttons). */}
      <ActionsModalPopup
        open={drawerOpen}
        onClose={showWorkspace ? handleExitWorkspace : toggleDrawer}
        title={
          isWithComponent
            ? "Add/Edit Product With Component"
            : isEdit
            ? "Edit Product Without Component"
            : "Add Product Without Component"
        }
        actions={showWorkspace ? [] : actions}
        width={showWorkspace ? "90%" : "60%"}
        titleSx={showWorkspace ? { fontFamily: "inherit" } : undefined}
      >
        {showWorkspace ? (
          <ComponentWorkspace
            articleNo={withComponentArticleNo}
            tcStandards={tcStandardsCache}
            productCategoryCode={stepData.productInfo.tcProductCategoryCode}
            productDetailCode={stepData.productInfo.tcProductDetailCode}
            productSupplementaryWeight={
              Number(stepData.productInfo.tcProductSupplementaryWeight) || 0
            }
            components={components}
            isSaving={componentSaving}
            isReturn={isReturn}
            onSaveComponent={handleSaveComponent}
            onDeleteComponent={handleDeleteComponent}
            onContinueToProduct={handleContinueToProduct}
            onExit={handleExitWorkspace}
          />
        ) : (
          <CustomStepper
            steps={steps}
            activeStep={activeStep}
            onStepChange={(step) => setActiveStep(step)}
          />
        )}
      </ActionsModalPopup>

      {/* Navigation Buttons */}
      {!drawerOpen ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            mt: 2,
            gap: "0.5rem",
          }}
        >
          {/* {!isNextEnabled && <InnerButton onClick={handleSave}>Save & Next</InnerButton>} */}
          <NavLink to="/v4/tc-dashboard">
            <InnerButton>Home</InnerButton>
          </NavLink>
          <NavLink to={`/v4/tc/${formtype}/shipment-details/${TE_ID}`}>
            <InnerButton sx={{ boxShadow: "none" }}>Back</InnerButton>
          </NavLink>
          {tableData?.length > 0 && (
            <Tooltip
              title={
                Object.keys(rmdfViolations).length > 0
                  ? "Fix RMDF product violations (Feedstock / Reclaimed Source) before proceeding"
                  : tableData.some((row: any) => row.isReturnAutofillDraft)
                  ? "Please edit and save the return TC products before proceeding"
                  : ""
              }
            >
              <span>
                <InnerButton
                  onClick={onNext}
                  disabled={
                    Object.keys(rmdfViolations).length > 0 ||
                    tableData.some((row: any) => row.isReturnAutofillDraft)
                  }
                >Next</InnerButton>
              </span>
            </Tooltip>
          )}
        </Box>
      ) : (
        <>
          {hasSubmitTc && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                mt: 2,
                gap: "0.5rem",
              }}
            >
              <NavLink to={`/v4/tc/${formtype}/shipment-details/${TE_ID}`}>
                <InnerButton sx={{ boxShadow: "none" }}>Back</InnerButton>
              </NavLink>
              <InnerButton onClick={onNext} sx={{ boxShadow: "none" }}>
                Next
              </InnerButton>
            </Box>
          )}
        </>
      )}

      {/* Confirmation Dialog */}
      <AlertPopup
        open={dialogOpen}
        title={popUpTitle}
        message={popUpMessage}
        onClose={handleCancelSave}
        onConfirm={handleConfirmSave}
        confirmText={popUpBtnName}
        cancelText="Cancel"
      />
    </>
  );
};

export default CertifiedProductsComponents;
